import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../auth/AuthContext.jsx";
import { useOktaSession } from "../okta/OktaSessionContext.jsx";
import { useArchitect } from "./useArchitect.js";
import {
  applyProposalGroup,
  applyProposalPolicy,
  createOktaUser,
  executeOktaAction,
  createCustomSamlApp,
  fetchOinAppSchema,
  installOinTemplateApp,
  fetchAuthenticatorsSummary,
  fetchGroupRulesSummary,
  listActiveAuthenticators,
  listGroupRules,
  searchCatalogApps,
} from "../services/oktaApi.js";
import { parseProposalObject, proposalHasGroupSlice, proposalHasPolicySlice } from "../utils/proposalHints.js";
import { isWriteIntent } from "../utils/isWriteIntent.mjs";
import {
  hasLeadingStaticTabIntroOnly,
  shouldRunWorkspaceBootstrap,
} from "./bootstrapPredicates.mjs";
import {
  shouldRefreshArchitectForAudit,
  withoutAuditNarrativeMessages,
} from "./auditArchitectSync.js";
import { auditFindingsFingerprint } from "../utils/auditFindingsFingerprint.js";
import { buildUsersFromCsv } from "../utils/csvToOktaUsers.js";
import { parseCSV } from "../utils/parseCSV.js";
import {
  buildOnboardingSubmitPrompt,
  loadOnboardingWizardFromStorage,
  ONBOARDING_WIZARD_START_OPTION_ID,
  saveOnboardingWizardToStorage,
} from "../config/onboardingWizard.js";
import {
  APPS_FIRST_DEFAULT_PATH,
  APPS_FIRST_INTENT_CHIPS,
  APPS_FIRST_INTENT_HEADLINE,
  detectAppsFirstIntent,
  hasApplicationIntegrationWrite,
} from "../utils/chatIntentApps.js";
import { getDailyAiRequestState } from "../utils/aiDailyRequests.js";
import { retrieveToken } from "../services/tokenVault.js";
import { useQuota } from "../context/QuotaContext.jsx";
import { useProjectOptional } from "../context/ProjectContext.jsx";
import { isEntraProject, isOrySelfHostedConnected, shouldSkipResumeModal } from "../utils/projectProvider.js";
import {
  isAuth0StubProject,
  isAuth0StubAssessment,
  auth0StubBootstrapMessage,
  auth0StubPostAssessmentMessage,
  auth0StubResumeMessage,
} from "../utils/credentiallessArchitect.js";
import { getOktaSessionSnapshot } from "../okta/oktaSessionStore.js";
import { orchestrateExecuteAction } from "../services/executionOrchestrator.js";
import {
  buildOinInstallBodyFromCurated,
  curatedFieldsToFormFields,
  initialValuesFromCuratedFields,
} from "../utils/oinCuratedForm.js";

/** sessionStorage — resume one chat attempt after `location.reload()` from Retry Now (hard reset). */
const HARD_RESET_CHAT_RETRY_KEY = "idwiz.hardResetChatRetry";

/** Shown when normal optionButtons apply; replaced by a single regenerate chip after failed JSON repair. */
const IDWIZ_REGENERATE_ANALYSIS_ID = "idwiz-regenerate-analysis";

/** Default task form shape (user create + OIN flows); keep keys stable for resets. */
const EMPTY_TASK_FORM = Object.freeze({
  email: "",
  firstName: "",
  lastName: "",
  instanceName: "",
  subdomain: "",
  baseUrl: "",
  oinQuery: "",
});

/** In-form OIN catalog table (chat history is hidden while the task form is open). */
const INITIAL_OIN_CATALOG_UI = Object.freeze({
  status: "idle",
  lastQuery: null,
  apps: [],
  searchPerformed: false,
  errorMessage: null,
  catalogSource: null,
  catalogNote: null,
});

/** Curated OIN configure step (below catalog results) when POST /api/idp/okta/oin-schema returns `curatedUiSchema.fields`. */
const INITIAL_OIN_CONFIGURE = Object.freeze({
  status: "idle",
  app: null,
  schemaPayload: null,
  formFields: [],
  fieldDefs: [],
  values: {},
  signOnMode: null,
  errorMessage: null,
  note: null,
});

/** Subdomain / base URL hints from chat for ServiceNow (e.g. idaxia → https://idaxia.service-now.com). */
function extractServiceNowHints(messages, memory) {
  const chunks = [];
  for (const m of messages || []) {
    if (m?.role === "user" && typeof m.content === "string") chunks.push(m.content);
  }
  for (const d of memory?.discoveryAnswers || []) {
    if (d?.answer) chunks.push(String(d.answer));
    if (d?.label) chunks.push(String(d.label));
  }
  if (memory?.projectName) chunks.push(String(memory.projectName));
  const blob = chunks.join("\n");
  const urlSn = blob.match(/https?:\/\/([a-z0-9][-a-z0-9]*)\.service-now\.com\b/i);
  if (urlSn) {
    const sub = urlSn[1];
    return { subdomain: sub, baseUrl: `https://${sub}.service-now.com` };
  }
  const meta = blob.match(/\b(?:instance|subdomain|tenant)\s*[:\s]+\s*([a-z0-9][-a-z0-9]{1,63})\b/i);
  if (meta) {
    const sub = meta[1];
    return { subdomain: sub, baseUrl: `https://${sub}.service-now.com` };
  }
  const loose = blob.match(/\b([a-z0-9][-a-z0-9]{2,40})\.servicenow\.com\b/i);
  if (loose) {
    const sub = loose[1];
    return { subdomain: sub, baseUrl: `https://${sub}.servicenow.com` };
  }
  const nearSn = blob.match(
    /\b([a-z0-9][-a-z0-9]{2,40})\b(?=\s*(?:instance|,|\.|\)|]|—|-)?\s*(?:for\s+)?servicenow\b)/i,
  );
  if (nearSn) {
    const sub = nearSn[1];
    return { subdomain: sub, baseUrl: `https://${sub}.service-now.com` };
  }
  return { subdomain: "", baseUrl: "" };
}

/** True when the user submitted an M365 / tenant-domain style form (not an app-creation form). */
function looksLikeM365TenantSubmission(vals) {
  const v = vals && typeof vals === "object" ? vals : {};
  if (v.appType != null || v.applicationType != null) return false;
  return (
    v.tenantDomain != null ||
    v.domain != null ||
    v.tenant != null ||
    v.microsoft365Domain != null ||
    v.TenantDomain != null
  );
}

/** True when values/fields indicate “create an app” (generic form from the architect). */
function looksLikeAppIntegrationSubmit(vals, fields) {
  const v = vals && typeof vals === "object" ? vals : {};
  const keys = Object.keys(v);
  const ids = new Set((fields || []).map((f) => f?.id).filter(Boolean));
  if (
    keys.some((k) => ["appType", "applicationType"].includes(k)) ||
    ids.has("appType") ||
    ids.has("applicationType")
  ) {
    return true;
  }
  const hasName =
    keys.some((k) => ["appName", "applicationName", "name"].includes(k)) ||
    ids.has("appName") ||
    ids.has("applicationName");
  const hasUrl =
    keys.some((k) => ["appUrl", "applicationUrl", "url", "ssoUrl", "ssoAcsUrl", "acsUrl"].includes(k)) ||
    ids.has("appUrl") ||
    ids.has("applicationUrl") ||
    ids.has("url");
  return Boolean(hasName && hasUrl);
}

const DOMAIN_FORM_KEYS = new Set(["domain", "salesforcedomain", "tenantdomain", "subdomain", "tenant"]);

/** Domain / tenant style forms (Salesforce SAML, generic domain capture). */
function formHasDomainSemantic(vals, fields) {
  const v = vals && typeof vals === "object" ? vals : {};
  if (Object.keys(v).some((k) => DOMAIN_FORM_KEYS.has(String(k).toLowerCase()))) return true;
  for (const f of fields || []) {
    const id = String(f?.id ?? "").toLowerCase();
    if (id.includes("domain") || id.includes("salesforce") || id.includes("tenant")) return true;
  }
  return false;
}

function pickDomainFormValue(vals, fields) {
  const v = vals && typeof vals === "object" ? vals : {};
  for (const k of Object.keys(v)) {
    if (DOMAIN_FORM_KEYS.has(String(k).toLowerCase())) {
      return String(v[k] ?? "").trim();
    }
  }
  for (const f of fields || []) {
    const id = String(f?.id ?? "").toLowerCase();
    if (id.includes("domain") || id.includes("salesforce") || id.includes("tenant")) {
      const fid = f.id != null ? String(f.id) : "";
      if (fid && v[fid] != null && String(v[fid]).trim()) return String(v[fid]).trim();
    }
  }
  const first = Object.values(v)[0];
  return first != null ? String(first).trim() : "";
}

function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result ?? ""));
    r.onerror = () => reject(r.error || new Error("Could not read file"));
    r.readAsText(file);
  });
}

function isCsvFile(file) {
  if (!file || typeof file.name !== "string") return false;
  const n = file.name.toLowerCase();
  if (n.endsWith(".csv")) return true;
  const t = String(file.type || "").toLowerCase();
  return t === "text/csv" || t === "application/csv" || t === "text/plain";
}

const DEMO_PROPOSED_JSON = `{
  "resource": "Policy",
  "id": "pol_00example",
  "name": "IDWiz — Conditional Access",
  "conditions": {
    "people": { "groups": { "include": ["grp_security"] } },
    "network": { "connection": "ANYWHERE" }
  },
  "actions": { "signon": { "access": "ALLOW", "requireFactor": true } }
}`;

const DEMO_PROPOSED_HCL = `resource "okta_policy_signon" "idwiz_example" {
  name = "IDWiz — Sign-on policy"
  status = "ACTIVE"
  priority = 1
}`;

/** Sidebar leaf → dashboard context for idwiz AI (sync with OktaTreeSidebar items). */
const LEAF_TO_CONTEXT = {
  People: "Users",
  Groups: "Groups",
  "Profile Editor": "Profile Editor",
  OIDC: "OIDC",
  SAML: "SAML",
  "API Services": "Apps",
  Authenticators: "Authenticators",
  "Authorization Servers": "Authorization Servers",
  Policies: "Policies",
};

/** Map sidebar path → short context label for AI + UI (e.g. Users, Apps). */
export function pathToContext(path) {
  if (!path) return "Unknown";
  const leaf = path.split(" > ").pop()?.trim() || path;
  return LEAF_TO_CONTEXT[leaf] ?? leaf;
}

/** Last segment of sidebar path (e.g. "People"). */
export function pathToLeaf(path) {
  if (!path) return "";
  return path.split(" > ").pop()?.trim() || "";
}

const CONCIERGE_WELCOME_BY_LEAF = {
  People:
    "Welcome to People. I can automate your onboarding, audit group rules, or cleanup stale accounts. Which should we tackle first?",
  Groups:
    "Welcome to Groups. I can help you tune group rules, membership, and lifecycle. Where should we begin?",
  "Profile Editor":
    "Welcome to Profile Editor. I can help you align attributes, mappings, and master sources. What should we review first?",
  OIDC:
    "Welcome to OIDC. I can help you register apps, tighten redirect URIs, and scope hygiene. Which should we tackle first?",
  SAML:
    "Welcome to SAML. I can help you harden federation, metadata, and sign-on flows. Where do you want to start?",
  "API Services":
    "Welcome to API Services. I can help you manage OAuth clients and API access posture. What should we focus on first?",
  Authenticators:
    "Welcome to Authenticators. I can help you strengthen MFA factors and enrollment policies. Which should we tackle first?",
  "Authorization Servers":
    "Welcome to Authorization Servers. I can help you refine scopes, policies, and tokens. Where should we begin?",
  Policies:
    "Welcome to Policies. I can help you tighten sign-on and access policies. What should we tackle first?",
};

function getConciergeWelcome(leaf) {
  const l = leaf || "People";
  if (CONCIERGE_WELCOME_BY_LEAF[l]) return CONCIERGE_WELCOME_BY_LEAF[l];
  return `Welcome to ${l}. I can help you review and improve this area. What would you like to focus on first?`;
}

/** Instant greeting when switching tabs — no AI round-trip. Full paths match OktaTreeSidebar `section > item`. */
const STATIC_TAB_GREETING = {
  "Directory > People":
    "Welcome to Users (People). I can help with onboarding, group rules, lifecycle, or account cleanup. Choose a suggested action below — idwiz AI runs only after you click one.",
  "Directory > Groups":
    "Welcome to Groups. I can help tune membership rules, lifecycle, and access mapping. Choose a suggested action — the assistant runs only after you select one.",
  "Directory > Profile Editor":
    "Welcome to Profile Editor. I can help align attributes, mappings, and master sources. Choose an action below — no cloud API until you click.",
  "Applications > OIDC":
    "Welcome to Apps (OIDC). I can help register apps, tighten redirect URIs, and scope hygiene. Pick a suggested action — the assistant only runs after you click.",
  "Applications > SAML":
    "Welcome to Apps (SAML). I can help with federation, metadata, and SSO. Use Configure SSO or another action below — idwiz AI runs only after you choose.",
  "Applications > API Services":
    "Welcome to Apps (API Services). I can help manage OAuth clients and API access. Pick a suggestion — no advisor API call until you click.",
  "Security > Authenticators":
    "Welcome to Authenticators. I can help strengthen MFA factors and enrollment. Choose an action — assistant calls only after you click.",
  "Security > Authorization Servers":
    "Welcome to Authorization Servers. I can help refine scopes, policies, and tokens. Choose below — no API until you click.",
  "Security > Policies":
    "Welcome to Policies. I can help tighten sign-on and access policies. Pick an action — idwiz AI runs only after you select one.",
};

function isOinSearchFormField(field) {
  if (!field || typeof field !== "object") return false;
  if (field.submitOnEnter === true) return true;
  if (field.role === "oin_search") return true;
  return false;
}

function getConciergeChips(leaf) {
  const l = leaf || "People";
  if (l === "People") {
    return [
      { id: "c-pe-1", label: "Automate onboarding", description: "Flows, lifecycle, and access" },
      { id: "add-single-user-manual", label: "Add a single user manually", description: "Okta profile: email, first & last name" },
      { id: "integrate-with-ad", label: "Integrate with AD", description: "Okta AD Agent, service account, delegated auth" },
      { id: "c-pe-2", label: "Review existing group rules", description: "SSWS table — loads before AI" },
      { id: "c-proceed-implementation", label: "Proceed with Implementation", description: "Execute when ready, or load rules" },
      { id: "c-pe-3", label: "Cleanup stale accounts", description: "Inactive or orphaned users" },
      { id: "csv-import-proceed", label: "Proceed with CSV Import", description: "Map First/Last/Email; overwrite vs skip" },
      { id: "c-pe-4", label: "Review sessions & MFA", description: "Session posture" },
    ];
  }
  if (l === "Groups") {
    return [
      { id: "c-gr-1", label: "Review existing group rules", description: "SSWS table — loads before AI" },
      { id: "c-proceed-implementation", label: "Proceed with Implementation", description: "Execute proposal or load rules" },
      { id: "integrate-with-ad", label: "Integrate with AD", description: "Okta AD Agent, service account, delegated auth" },
      { id: "c-gr-2", label: "Security audit", description: "Read-only posture" },
      { id: "c-gr-3", label: "Terraform blueprint", description: "Inspector HCL" },
      { id: "c-gr-4", label: "Plan rollout", description: "Phased validation" },
    ];
  }
  if (l === "Policies") {
    return [
      { id: "c-proceed-implementation", label: "Proceed with Implementation", description: "Run supported lifecycle steps in Okta" },
      { id: "c-review-existing-rules", label: "Review existing group rules", description: "Membership rules via SSWS" },
      { id: "c-pol-1", label: "Sign-on policy hardening", description: "MFA, network, people" },
      { id: "c-pol-2", label: "Security audit", description: "Read-only posture" },
      { id: "c-pol-3", label: "Terraform blueprint", description: "Inspector HCL" },
      { id: "c-pol-4", label: "Plan rollout", description: "Phased validation" },
    ];
  }
  if (l === "SAML") {
    return [
      { id: "configure-sso", label: "Configure SSO", description: "Metadata, ACS URLs, federation — runs idwiz AI" },
      { id: "c-saml-1", label: "Harden SAML assertions", description: "Signing, encryption, audience restrictions" },
      { id: "c-saml-2", label: "Security audit", description: "Read-only posture" },
      { id: "c-saml-3", label: "Terraform blueprint", description: "Inspector HCL" },
    ];
  }
  const ctx = pathToContext(`Directory > ${l}`);
  return [
    { id: "c-gen-1", label: `Map ${ctx} scope`, description: "Inventory & priorities" },
    { id: "c-gen-2", label: "Security audit", description: "Read-only posture" },
    { id: "c-gen-3", label: "Terraform blueprint", description: "Inspector HCL" },
    { id: "c-gen-4", label: "Plan rollout", description: "Phased validation" },
  ];
}

/** Normalize proposedConfig from API for Inspector pane. */
function normalizeProposedConfig(raw) {
  if (!raw || typeof raw !== "object") return null;
  const format = raw.format === "hcl" ? "hcl" : "json";
  let body = raw.body;
  if (body == null) return null;
  if (typeof body === "object") {
    body = JSON.stringify(body, null, 2);
  } else {
    body = String(body);
  }
  const trimmed = body.trim();
  if (!trimmed) return null;
  return { format, body: trimmed };
}

/** Merge JSON configs incrementally for Inspector + memory. */
function mergeProposedConfigIncremental(prev, incoming) {
  if (!incoming) return prev;
  if (!prev) return incoming;
  if (prev.format !== incoming.format) return incoming;
  if (prev.format === "hcl") return incoming;
  try {
    const a = JSON.parse(prev.body);
    const b = JSON.parse(incoming.body);
    if (
      typeof a === "object" &&
      a &&
      typeof b === "object" &&
      b &&
      !Array.isArray(a) &&
      !Array.isArray(b)
    ) {
      return { format: "json", body: JSON.stringify({ ...a, ...b }, null, 2) };
    }
  } catch {
    /* fall through */
  }
  return incoming;
}

function buildOktaUserCreateBody(values) {
  const email = (values.email || "").trim();
  return {
    profile: {
      firstName: (values.firstName || "").trim(),
      lastName: (values.lastName || "").trim(),
      email,
      login: email || "",
    },
  };
}

function parseProfileFromProposedBody(bodyStr) {
  try {
    const o = JSON.parse(bodyStr);
    const p = o?.profile || {};
    return {
      email: p.email != null ? String(p.email) : "",
      firstName: p.firstName != null ? String(p.firstName) : "",
      lastName: p.lastName != null ? String(p.lastName) : "",
    };
  } catch {
    return { email: "", firstName: "", lastName: "" };
  }
}

const DAILY_LIMIT_SESSION_KEY = "idwiz.dailyLimitReached";
/** After a TPM quota error, stagger Execute / bulk requests for this long. */
const TPM_SOFT_THROTTLE_WINDOW_MS = 15 * 60 * 1000;

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

const CONNECT_OKTA_FIRST_MSG =
  "Please connect your Okta tenant first — enter your org URL and SSWS API token in the tenant panel and choose **Validate token**, then try again.";

/** Discovery chips that configure or search catalog apps — need a live Okta session. */
const OPTION_IDS_REQUIRING_OKTA_TENANT = new Set([
  "configure-sso",
  "intent-create-oidc-app",
  "intent-configure-saml",
  "intent-browse-app-catalog",
  "intent-add-servicenow-oin",
]);

function optionRequiresOktaTenant(optionId) {
  return optionId != null && OPTION_IDS_REQUIRING_OKTA_TENANT.has(String(optionId));
}

function mergeQuotaMessage(data) {
  if (!data || typeof data !== "object") return "";
  const parts = [
    data.message,
    data.details,
    typeof data.error === "string" ? data.error : null,
  ].filter((x) => x != null && String(x).trim() !== "");
  return parts.map((c) => String(c)).join(" ").trim();
}

function parseQuotaKind(quotaMsg) {
  const m = String(quotaMsg || "");
  if (/quota exceeded.*?\btpm\b/i.test(m)) return "tpm";
  if (/quota exceeded.*?\brpd\b/i.test(m)) return "rpd";
  if (/quota exceeded.*?\brpm\b/i.test(m)) return "rpm";
  return null;
}

function formatArchitectureSteps(steps) {
  if (!Array.isArray(steps) || !steps.length) return "";
  return steps
    .map((s) => {
      if (!s || typeof s !== "object") return "";
      const kind = s.kind === "recommendation" ? "Recommendation" : "Requirement";
      const title = s.title != null ? String(s.title).trim() : "";
      const detail = s.detail != null ? String(s.detail).trim() : "";
      const head = title ? `${kind}: ${title}` : kind;
      return detail ? `${head}\n${detail}` : head;
    })
    .filter(Boolean)
    .join("\n\n");
}

function normalizeOptionButtons(optionButtons, nextSteps) {
  const out = [];
  if (Array.isArray(optionButtons)) {
    optionButtons.forEach((o, i) => {
      if (typeof o === "string") {
        out.push({ id: `opt-${i}`, label: o, description: "" });
        return;
      }
      if (!o || typeof o !== "object") return;
      const id = o.id != null ? String(o.id) : `opt-${i}`;
      const label = o.label != null ? String(o.label) : "";
      if (!label.trim()) return;
      out.push({
        id,
        label,
        description: o.description != null ? String(o.description) : "",
      });
    });
  }
  if (out.length) return out.slice(0, 6);
  if (Array.isArray(nextSteps)) {
    return nextSteps.slice(0, 6).map((label, i) => ({
      id: `step-${i}`,
      label: String(label),
      description: "",
    }));
  }
  return [];
}

function buildSystemInstructionNav({ prevCtx, newCtx, memory, path }) {
  const memParts = [];
  if (memory.projectName) memParts.push(`Active project: "${memory.projectName}".`);
  if (memory.facts?.length) memParts.push(`Notes: ${memory.facts.join("; ")}`);
  if (memory.discoveryAnswers?.length) {
    memParts.push(`Prior discovery steps: ${memory.discoveryAnswers.length} recorded.`);
  }
  const memoryLine = memParts.length ? ` ${memParts.join(" ")}` : "";

  return [
    "[SYSTEM — hidden context update — do not quote this prefix to the user]",
    `The user navigated within the Okta Admin mirror: from "${prevCtx}" to "${newCtx}" (path: ${path}).`,
    `Prioritize guidance relevant to "${newCtx}".${memoryLine}`,
    "Maintain continuity with any active project or prior user-stated goals across context switches.",
    "Respond concisely as idwiz AI; offer one proactive next step when appropriate.",
  ].join(" ");
}

function ingestMemoryFromUserText(text, prev) {
  const next = {
    ...prev,
    facts: [...(prev.facts || [])],
    discoveryAnswers: [...(prev.discoveryAnswers || [])],
    sswsInterview: prev.sswsInterview || { readOnlyTerraformMode: false },
  };
  const lower = text.toLowerCase();

  if (/marketing project/i.test(lower)) {
    next.projectName = next.projectName || "Marketing Project";
  }
  const projectQuoted = text.match(/project\s+(?:is\s+)?["']([^"']+)["']/i);
  if (projectQuoted?.[1]) next.projectName = projectQuoted[1].trim();
  const projectColon = text.match(/project\s*:\s*([^\n.]+)/i);
  if (projectColon?.[1]) next.projectName = projectColon[1].trim();

  return next;
}

function newId() {
  return `m${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

const GROUP_RULES_EMPTY_MSG =
  "I am now showing zero active group rules in the table below. Use the suggested actions to create your first automation rule.";

/** Sent as userMessage to the architect — applySuccessfulAgentResponse shows the reply (not suppressed). */
/** User enabled CSV file picker via "Proceed with CSV Import" or onboarding "CSV" source. */
export const ACTIVE_WORKFLOW_CSV_IMPORT = "CSV_IMPORT";
const AUTH_ACTIVE_FACTORS_AUDIT_TAG = "[AUTH_ACTIVE_FACTORS_AUDIT]";

const GROUP_RULES_AUDIT_PROMPT_TAG = "[idwiz internal — group rules policy audit v1]";

function wantsActiveFactorsAudit(text, ctx) {
  const t = String(text || "");
  const c = String(ctx || "");
  // Only fire when the user is clearly asking for an MFA/authenticator audit/listing.
  if (!/\b(authenticator|authenticators|factor|factors|mfa|2fa|webauthn|fido|okta\s+verify)\b/i.test(t)) return false;
  if (!/\b(active|enabled|status|audit|list|show|review|what)\b/i.test(t)) return false;
  // Stronger intent when user is in Security/Policies, but allow anywhere.
  if (/\b(app|application|sso)\b/i.test(c) && !/\b(authenticator|factor|mfa|2fa)\b/i.test(t)) return false;
  return true;
}

function buildGroupRulesAuditUserText(groupRulesSummary, displayRowsCount) {
  const summaryJson = JSON.stringify(groupRulesSummary, null, 2);
  const instr = `Do NOT merely confirm that you are showing or displaying this data. Analyze the provided JSON for security gaps, naming inconsistencies, redundant or seemingly unused rules, and risky membership/assignment patterns.

Respond with JSON only (idwiz schema). The "message" field must:
- Start with the title line: Group rules audit
- Then exactly three bullet points (use leading "- " for each) with concrete findings from the JSON
- Then one line starting with "Recommendation:" followed by one clear, actionable recommendation

Forbidden in "message": claiming you are loading, showing, or rendering a table or UI.

Also include proposedConfig: null, activeTask: "review_group_rules", and optionButtons: 3–4 concise next steps for the user.

Okta group rules structured summary (${displayRowsCount ?? 0} row(s) in the local table):\n${summaryJson}`;

  return `${GROUP_RULES_AUDIT_PROMPT_TAG}

The group rules table in idwiz is populated from SSWS fetch. This summary is structured server-side to avoid dumping raw rule objects into the prompt. ${instr}`;
}

/** ACTIVE rules only (Okta may return INACTIVE). */
function mapActiveGroupRulesFromResponse(rulesArr) {
  const raw = Array.isArray(rulesArr) ? rulesArr : [];
  return raw
    .filter((r) => !r?.status || String(r.status).toUpperCase() === "ACTIVE")
    .map((r) => ({
      id: r?.id != null ? String(r.id) : "",
      name: r?.name != null ? String(r.name).trim() || "(unnamed rule)" : "(unnamed rule)",
      status: r?.status != null ? String(r.status) : "ACTIVE",
    }));
}

/**
 * Central state for the IDWiz AI Command Center.
 * Proactive agent: context intros → option buttons + hybrid text input.
 */
export function useIdwiz() {
  const { currentCapabilities, canCreate } = useAuth();
  const { oktaConnected, orgUrl: oktaOrgUrl, apiToken: oktaApiToken, setSession: setOktaSession } = useOktaSession();
  const projectCtx = useProjectOptional();
  const project = projectCtx?.project ?? null;
  const projectApiToken = projectCtx?.apiToken ?? null;
  const auditData = projectCtx?.auditData ?? null;
  const driftData = projectCtx?.driftData ?? null;
  const updateRoadmapItemFromCtx = projectCtx?.updateRoadmapItem;
  const addRoadmapItemFromCtx = projectCtx?.addRoadmapItem;
  const syncProjectFromCtx = projectCtx?.syncProject;
  const appendLogFromCtx = projectCtx?.appendLog;
  const loadProjectFromCtx = projectCtx?.loadProject;

  const projectSyncRef = useRef({
    project: null,
    updateRoadmapItem: null,
    addRoadmapItem: null,
    syncProject: null,
  });
  const auditDataRef = useRef(null);
  const driftDataRef = useRef(null);
  useEffect(() => {
    projectSyncRef.current = {
      project,
      updateRoadmapItem: updateRoadmapItemFromCtx,
      addRoadmapItem: addRoadmapItemFromCtx,
      syncProject: syncProjectFromCtx,
    };
    auditDataRef.current = auditData;
    driftDataRef.current = driftData;
  }, [project, updateRoadmapItemFromCtx, addRoadmapItemFromCtx, syncProjectFromCtx, auditData, driftData]);

  const [phase, setPhase] = useState("gathering");
  const phaseRef = useRef("gathering");
  const [manualUserSubmitting, setManualUserSubmitting] = useState(false);
  const [csvBulkSubmitting, setCsvBulkSubmitting] = useState(false);
  const [showCsvButton, setShowCsvButton] = useState(false);
  const [activeWorkflow, setActiveWorkflow] = useState(null);
  const showCsvButtonRef = useRef(false);
  const activeWorkflowRef = useRef(null);
  useEffect(() => {
    showCsvButtonRef.current = showCsvButton;
  }, [showCsvButton]);
  useEffect(() => {
    activeWorkflowRef.current = activeWorkflow;
  }, [activeWorkflow]);
  // Clean context: if workflow is empty on a good render, default to GENERAL.
  useEffect(() => {
    if (!activeWorkflow) setActiveWorkflow("GENERAL");
  }, [activeWorkflow]);
  const [servicenowAppSubmitting, setServicenowAppSubmitting] = useState(false);
  const [customSamlSubmitting, setCustomSamlSubmitting] = useState(false);
  const [appIntegrationSubmitting, setAppIntegrationSubmitting] = useState(false);
  /** Which OIN result row is currently calling POST /api/idp/okta/apps/oin (app id or template key). */
  const [oinAddingAppId, setOinAddingAppId] = useState(null);
  const [oinCatalogSearchBusy, setOinCatalogSearchBusy] = useState(false);
  const [oinCatalogUi, setOinCatalogUi] = useState(() => ({ ...INITIAL_OIN_CATALOG_UI }));
  const [oinConfigureUi, setOinConfigureUi] = useState(() => ({ ...INITIAL_OIN_CONFIGURE }));
  const [oinConfigureSubmitting, setOinConfigureSubmitting] = useState(false);
  /** Prevents overlapping OIN search requests (double click / rapid Enter before busy state commits). */
  const oinSearchInFlightRef = useRef(false);
  /** Shown during generic proposal → Okta automation steps */
  const [executionProgress, setExecutionProgress] = useState(null);
  const csvPendingRef = useRef(null);
  /** Guided "Automate onboarding" — static steps; architect only on final submit */
  const [onboardingWizard, setOnboardingWizard] = useState(null);
  const onboardingWizardRef = useRef(null);
  /** Brief UI state while a single automatic JSON-repair request is in flight (no red error box). */
  const [refiningAgentResponse, setRefiningAgentResponse] = useState(false);
  const [selectedTreePath, setSelectedTreePath] = useState("Directory > People");
  const [currentContext, setCurrentContext] = useState(() => pathToContext("Directory > People"));
  const [hiddenSystemEvents, setHiddenSystemEvents] = useState([]);
  const {
    messages,
    setMessages,
    isThinking: thinking,
    setIsThinking: setThinking,
    activeOptionButtons,
    setActiveOptionButtons,
    activeFormFields,
    setActiveFormFields,
    primaryActionLabel: architectPrimaryActionLabel,
    setPrimaryActionLabel: setArchitectPrimaryActionLabel,
    activeTask,
    setActiveTask,
    proposedConfig,
    setProposedConfig,
    conversationMemory,
    setConversationMemory,
    sendMessage: architectSendMessage,
    warning,
    bestPracticeViolation,
    dismissWarning,
    roadmapUpdate: architectRoadmapUpdate,
    memoryUpdate: architectMemoryUpdate,
    clearRoadmapUpdate,
    clearMemoryUpdate,
  } = useArchitect({
    selectedTreePath,
    currentContext,
    hiddenSystemEvents,
    project,
    apiToken: projectApiToken,
    refreshProject: loadProjectFromCtx,
    getAuditFindings: () =>
      projectCtx?.auditData?.findings ?? projectCtx?.project?.auditSnapshot?.findings ?? undefined,
  });
  const hiddenRef = useRef([]);
  const messagesRef = useRef([]);
  const selectedTreePathRef = useRef(selectedTreePath);
  const memoryRef = useRef(conversationMemory);
  const agentSeqRef = useRef(0);
  const lastAgentUserTextRef = useRef("");
  /** When true, next `runContextIntro` skips welcome/chips (e.g. chat-driven path pivot). */
  const skipNextContextIntroRef = useRef(false);
  const inputRef = useRef(null);
  const pendingInputBufferRef = useRef("");
  const runAgentRef = useRef(null);
  const approveExecuteReadyRef = useRef(false);
  const approveAndExecuteRef = useRef(null);
  const [sendReadyPulse, setSendReadyPulse] = useState(false);
  const [pendingInputBuffer, setPendingInputBuffer] = useState("");
  const [rateLimitError, setRateLimitError] = useState(null);
  /** True after the last /api/ai/chat response was HTTP 429 (any limiter type). Cleared on 200 OK or manual retry. */
  const [chatHttp429Active, setChatHttp429Active] = useState(false);
  const [inputWakeBounce, setInputWakeBounce] = useState(false);

  /** User-stated focus (e.g. apps-first); cleared on normal tab intro. */
  const [commandIntentLabel, setCommandIntentLabel] = useState(null);
  const [input, setInput] = useState("");
  const deferredContextIntroRef = useRef(false);
  /** Run tab intro only on real path changes (not when a send lock defers intro). */
  const pathForIntroEffectRef = useRef(selectedTreePath);
  const isFirstPathIntroEffectRef = useRef(true);
  const tpmThrottleUntilRef = useRef(0);
  const dailyLimitReachedRef = useRef(false);
  const [dailyLimitReached, setDailyLimitReached] = useState(() => {
    try {
      return sessionStorage.getItem(DAILY_LIMIT_SESSION_KEY) === "1";
    } catch {
      return false;
    }
  });
  const { isSendLocked: quotaSendLocked, triggerRateLimit, clearRateLimit: forceClearSendLock } = useQuota();

  useEffect(() => {
    if (!architectRoadmapUpdate || !project?.projectId) return;
    const { action, item } = architectRoadmapUpdate;
    if (!item?.id) {
      clearRoadmapUpdate();
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        if (action === "add" && addRoadmapItemFromCtx) await addRoadmapItemFromCtx(item);
        else if (updateRoadmapItemFromCtx) await updateRoadmapItemFromCtx(item.id, item);
      } catch (e) {
        console.warn("[useIdwiz] roadmap sync (architect)", e);
      } finally {
        if (!cancelled) clearRoadmapUpdate();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    architectRoadmapUpdate,
    project?.projectId,
    addRoadmapItemFromCtx,
    updateRoadmapItemFromCtx,
    clearRoadmapUpdate,
  ]);

  useEffect(() => {
    if (!architectMemoryUpdate || !project?.projectId || !syncProjectFromCtx) return;
    const mu = architectMemoryUpdate;
    let cancelled = false;
    (async () => {
      try {
        await syncProjectFromCtx({
          discoveryAnswers: {
            ...(project.discoveryAnswers && typeof project.discoveryAnswers === "object" ? project.discoveryAnswers : {}),
            ...(mu.discoveryAnswers && typeof mu.discoveryAnswers === "object" ? mu.discoveryAnswers : {}),
          },
          businessContext: {
            ...(project.businessContext && typeof project.businessContext === "object" ? project.businessContext : {}),
            ...(mu.businessContext && typeof mu.businessContext === "object" ? mu.businessContext : {}),
          },
          discoveryComplete: mu.discoveryComplete ?? project.discoveryComplete,
          discoveryProgress: mu.discoveryProgress ?? project.discoveryProgress,
        });
      } catch (e) {
        console.warn("[useIdwiz] memory sync (architect)", e);
      } finally {
        if (!cancelled) clearMemoryUpdate();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [architectMemoryUpdate, project, syncProjectFromCtx, clearMemoryUpdate]);

  const sendArchitectMessage = useCallback(
    async (text, opts = {}) => {
      const isSystem =
        opts._isSystemMessage === true ||
        (typeof text === "string" && /^\[SYSTEM:/i.test(text.trim()));
      const res = await architectSendMessage(text, {
        ...opts,
        _isSystemMessage: isSystem,
        gracefulDegradeOnFailure: opts.gracefulDegradeOnFailure ?? isSystem,
        project: opts.project ?? project ?? undefined,
        auditFindings: opts.auditFindings ?? auditData?.findings,
        driftChanges: opts.driftChanges ?? driftData?.changes ?? undefined,
      });
      if (opts.markAuditNarrative) {
        setMessages((prev) => {
          const next = [...prev];
          for (let i = next.length - 1; i >= 0; i -= 1) {
            const m = next[i];
            if (m?.role === "assistant" && !m.hidden) {
              next[i] = { ...m, auditNarrative: true };
              break;
            }
          }
          messagesRef.current = next;
          return next;
        });
      }
      return res;
    },
    [architectSendMessage, project, auditData, driftData, setMessages],
  );

  const endSession = useCallback(async () => {
    if (!project?.projectId || messagesRef.current.length < 4) return;
    try {
      await fetch(`/api/projects/${encodeURIComponent(project.projectId)}/summarize`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messages: messagesRef.current }),
      });
    } catch (err) {
      console.warn("Session summarization failed:", err);
    }
  }, [project?.projectId]);

  useEffect(() => {
    const onBeforeUnload = () => {
      void endSession();
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [endSession]);

  const inactivityTimerRef = useRef(null);
  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) window.clearTimeout(inactivityTimerRef.current);
    inactivityTimerRef.current = window.setTimeout(() => {
      void endSession();
    }, 30 * 60 * 1000);
  }, [endSession]);

  useEffect(() => {
    resetInactivityTimer();
    return () => {
      if (inactivityTimerRef.current) window.clearTimeout(inactivityTimerRef.current);
    };
  }, [resetInactivityTimer]);

  const workspaceBootstrappedRef = useRef(false);
  const architectAuditFpRef = useRef(null);

  useEffect(() => {
    workspaceBootstrappedRef.current = false;
    architectAuditFpRef.current = null;
  }, [project?.projectId]);

  const syncAfterAssessment = useCallback(
    async (audit, drift) => {
      if (audit && typeof projectCtx?.setAuditData === "function") {
        projectCtx.setAuditData(audit);
      }
      if (typeof projectCtx?.setDriftData === "function") {
        projectCtx.setDriftData(drift ?? null);
      }
      if (typeof loadProjectFromCtx === "function") {
        await loadProjectFromCtx();
      }
    },
    [projectCtx, loadProjectFromCtx],
  );

  useEffect(() => {
    if (!project?.projectId) return;
    const token = projectApiToken || oktaApiToken;
    if (!shouldSkipResumeModal(project) && !token) return;

    const effectiveAudit = auditData ?? project.auditSnapshot ?? null;
    const fp = auditFindingsFingerprint(effectiveAudit?.findings);
    const bootstrapEligible = shouldRunWorkspaceBootstrap(messages);
    const fpRefresh = shouldRefreshArchitectForAudit(architectAuditFpRef.current, fp, {
      workspaceBootstrapped: workspaceBootstrappedRef.current,
    });

    if (!bootstrapEligible && !fpRefresh) return;
    if (workspaceBootstrappedRef.current && !fpRefresh) return;
    if (fpRefresh && !fp) return;

    const replaceStaticIntro = bootstrapEligible && hasLeadingStaticTabIntroOnly(messages);

    void (async () => {
      try {
        if (fpRefresh) {
          setMessages((prev) => {
            const next = withoutAuditNarrativeMessages(prev);
            messagesRef.current = next;
            return next;
          });
          setActiveOptionButtons([]);
        } else if (replaceStaticIntro) {
          setMessages([]);
          messagesRef.current = [];
          setActiveOptionButtons([]);
        }

        const providerId = project.providerId || "okta";

        if (isAuth0StubProject(project)) {
          workspaceBootstrappedRef.current = true;
          architectAuditFpRef.current = fp || architectAuditFpRef.current;
          const staticText = isAuth0StubAssessment(effectiveAudit)
            ? auth0StubPostAssessmentMessage(providerId)
            : project.conversationSummary?.summary
              ? auth0StubResumeMessage(providerId)
              : auth0StubBootstrapMessage(providerId);
          setMessages((prev) => {
            const next = [
              ...prev,
              {
                role: "assistant",
                kind: "consultation",
                content: staticText,
                ...(isAuth0StubAssessment(effectiveAudit) ? { auditNarrative: true } : {}),
              },
            ];
            messagesRef.current = next;
            return next;
          });
          setActiveOptionButtons([]);
        } else if (isOrySelfHostedConnected(project) && !effectiveAudit?.findings) {
          workspaceBootstrappedRef.current = true;
          setMessages((prev) => prev);
          setActiveOptionButtons([]);
        } else if (effectiveAudit?.findings) {
          workspaceBootstrappedRef.current = true;
          architectAuditFpRef.current = fp;
          await sendArchitectMessage(
            fpRefresh ? "[SYSTEM: Assessment updated]" : "[SYSTEM: Initial analysis complete]",
            {
              interactionMode: effectiveAudit.isGreenfield ? "greenfield" : "tenant_connected",
              auditFindings: effectiveAudit.findings,
              markAuditNarrative: true,
            },
          );
        } else if (!fpRefresh) {
          workspaceBootstrappedRef.current = true;
          if (project.conversationSummary?.summary) {
            await sendArchitectMessage("[SYSTEM: Resuming project session]", {
              interactionMode: "resume",
            });
          } else {
            await sendArchitectMessage("[SYSTEM: Project loaded, beginning discovery]", {
              interactionMode: "discovery",
            });
          }
        }
      } catch (e) {
        console.warn("[useIdwiz] workspace bootstrap", e);
        if (!fpRefresh) workspaceBootstrappedRef.current = false;
      }
    })();
  }, [
    project,
    projectApiToken,
    oktaApiToken,
    auditData,
    messages,
    sendArchitectMessage,
    setMessages,
    setActiveOptionButtons,
  ]);

  /** Last chat payload when HTTP 429 — user must click Retry Now (no auto-resubmit). */
  const lastFailedChatAttemptRef = useRef(null);
  const [manualRetryPending, setManualRetryPending] = useState(false);

  /** Local calendar-day AI request counter (localStorage-backed). */
  const [dailyAiRequestCount, setDailyAiRequestCount] = useState(() => getDailyAiRequestState().count);
  const activeTaskRef = useRef(null);
  useEffect(() => {
    activeTaskRef.current = activeTask;
  }, [activeTask]);

  const roadmapStaleSpinnerResetSkipRef = useRef(true);
  useEffect(() => {
    if (roadmapStaleSpinnerResetSkipRef.current) {
      roadmapStaleSpinnerResetSkipRef.current = false;
      return;
    }
    const upd = projectSyncRef.current.updateRoadmapItem;
    const pr = projectSyncRef.current.project;
    if (!upd || !Array.isArray(pr?.roadmap?.items)) return;
    const recent = messagesRef.current.slice(-4);
    const isCurrentlyWorkingOn = (itemId) => {
      const needle = String(itemId).replace(/-/g, " ").toLowerCase();
      return recent.some((m) => String(m.content ?? "").toLowerCase().includes(needle));
    };
    for (const i of pr.roadmap.items) {
      const st = String(i.status ?? "")
        .toLowerCase()
        .replace(/\s+/g, "_");
      if (st !== "in_progress") continue;
      const id = i.id != null ? String(i.id) : "";
      if (!id) continue;
      if (!isCurrentlyWorkingOn(id)) void upd(id, { status: "available" });
    }
  }, [activeTask]);

  /** Local Okta group rules fetch; a structured summary (not raw arrays) is sent to the architect for the one-shot audit prompt. */
  const [groupRulesView, setGroupRulesView] = useState({
    status: "idle",
    rules: [],
    error: null,
    fetchedAt: null,
    progressSteps: null,
  });
  const groupRulesViewRef = useRef(groupRulesView);
  /** Last ACTIVE rules array from Okta (full objects) — reuse for RPM when AI asks to review again without refetch. */
  const groupRulesAuditRawRef = useRef(null);
  /** True while OIN search task/form is active — apply Applications context once on entry. */
  const oinWorkflowActiveRef = useRef(false);
  useEffect(() => {
    groupRulesViewRef.current = groupRulesView;
  }, [groupRulesView]);
  const [taskFormValues, setTaskFormValues] = useState(() => ({ ...EMPTY_TASK_FORM }));
  const taskFormValuesRef = useRef(taskFormValues);
  useEffect(() => {
    taskFormValuesRef.current = taskFormValues;
  }, [taskFormValues]);

  const formActive = Boolean(activeFormFields?.length);

  /** Primary action on DynamicForm works without the main “Execute” gate (OIN, manual tasks, SAML, app-create). */
  const taskFormUsesInlinePrimary = useMemo(() => {
    if (
      activeTask === "manual_user_create" ||
      activeTask === "oin_servicenow" ||
      activeTask === "CUSTOM_SAML"
    ) {
      return true;
    }
    return Array.isArray(activeFormFields) && activeFormFields.length > 0;
  }, [activeTask, activeFormFields]);

  const preserveAutomationRef = useRef({});
  useEffect(() => {
    preserveAutomationRef.current = {
      savedAt: Date.now(),
      activeFormFields,
      taskFormValues,
      activeTask,
      proposedConfig,
      conversationMemorySnapshot: {
        proposedConfigDraft: conversationMemory.proposedConfigDraft,
        consultationMode: conversationMemory.consultationMode,
      },
    };
  }, [
    activeFormFields,
    taskFormValues,
    activeTask,
    proposedConfig,
    conversationMemory.proposedConfigDraft,
    conversationMemory.consultationMode,
  ]);

  const tryRestoreAutomationPreserve = useCallback(() => {
    try {
      const raw = sessionStorage.getItem("idwiz.automationPreserve");
      if (!raw) return;
      const s = JSON.parse(raw || "{}");
      if (Date.now() - (s.savedAt || 0) > 60 * 60 * 1000) {
        sessionStorage.removeItem("idwiz.automationPreserve");
        return;
      }
      sessionStorage.removeItem("idwiz.automationPreserve");
      if (s.proposedConfig != null) setProposedConfig(s.proposedConfig);
      if (Array.isArray(s.activeFormFields) && s.activeFormFields.length > 0) {
        setActiveFormFields(s.activeFormFields);
      }
      if (s.taskFormValues && typeof s.taskFormValues === "object") {
        setTaskFormValues({ ...EMPTY_TASK_FORM, ...s.taskFormValues });
      }
      if (s.activeTask) setActiveTask(s.activeTask);
      if (s.conversationMemorySnapshot && typeof s.conversationMemorySnapshot === "object") {
        setConversationMemory((prev) => ({
          ...prev,
          ...s.conversationMemorySnapshot,
        }));
      }
    } catch (_) {
      try {
        sessionStorage.removeItem("idwiz.automationPreserve");
      } catch (_) {
        /* ignore */
      }
    }
  }, []);

  const clearRateLimitPadding = useCallback(() => {}, []);

  const cleanupRateLimitToReady = useCallback(() => {
    clearRateLimitPadding();
    setRateLimitError(null);
    setChatHttp429Active(false);
    setInputWakeBounce(false);
    setSendReadyPulse(false);
  }, [clearRateLimitPadding]);

  /** No client-side send block; only clears padding bookkeeping (no rate-limit flags). */
  const releaseRateLimitGate = useCallback(() => {
    clearRateLimitPadding();
  }, [clearRateLimitPadding]);

  const clearDashboardTabErrorState = useCallback(() => {
    setRateLimitError(null);
    setChatHttp429Active(false);
    blockContextIntroAfter429Ref.current = false;
  }, []);

  const readOnlyTerraformMode = Boolean(conversationMemory?.sswsInterview?.readOnlyTerraformMode);

  const canApproveExecute = useMemo(() => {
    if (readOnlyTerraformMode) return false;
    const caps = currentCapabilities;
    if (!caps?.userId || !caps?.tabAccess) return false;
    if (activeTask === "oin_servicenow") {
      return hasApplicationIntegrationWrite(caps);
    }
    return caps.tabAccess[currentContext]?.canWrite === true;
  }, [activeTask, currentCapabilities, currentContext, readOnlyTerraformMode]);

  const hasProposedBody = Boolean(proposedConfig?.body?.trim());

  const sswsValidated = useMemo(
    () => Boolean(currentCapabilities?.userId) && !readOnlyTerraformMode,
    [currentCapabilities?.userId, readOnlyTerraformMode],
  );

  /** Validated SSWS + tab write, or validated SSWS + executable proposal / ServiceNow task (execution bridge). */
  const canTriggerExecute = useMemo(() => {
    if (readOnlyTerraformMode) return false;
    if (!sswsValidated) return false;
    if (canApproveExecute) return true;
    if (hasProposedBody) return true;
    if (activeTask === "oin_servicenow") return true;
    return false;
  }, [readOnlyTerraformMode, sswsValidated, canApproveExecute, hasProposedBody, activeTask]);

  /**
   * Force-mode hook fix: only transition Consultation → Execution from an effect.
   * This prevents render-time state writes and avoids infinite re-render loops.
   */
  useEffect(() => {
    if (!canTriggerExecute) return;
    if (conversationMemory?.consultationMode === false) return;
    setConversationMemory((m) => (m?.consultationMode === false ? m : { ...m, consultationMode: false }));
  }, [canTriggerExecute, conversationMemory?.consultationMode]);

  const manualUserMandatoryComplete = useMemo(() => {
    if (activeTask !== "manual_user_create") return true;
    return Boolean(
      taskFormValues.email?.trim() &&
        taskFormValues.firstName?.trim() &&
        taskFormValues.lastName?.trim(),
    );
  }, [activeTask, taskFormValues]);

  /** Execute: tab write or SSWS bridge; task-specific completeness below. */
  const approveExecuteReady = useMemo(() => {
    if (!canTriggerExecute) return false;
    if (activeTask === "manual_user_create") return manualUserMandatoryComplete;
    if (activeTask === "csv_user_import") {
      try {
        const o = JSON.parse(proposedConfig?.body || "{}");
        return Array.isArray(o.users) && o.users.length > 0;
      } catch {
        return false;
      }
    }
    if (activeTask === "oin_servicenow") {
      const i = taskFormValues.instanceName?.trim();
      const s = taskFormValues.subdomain?.trim();
      const b = taskFormValues.baseUrl?.trim();
      if (!i || !s || !b) return false;
      try {
        const u = new URL(b);
        return u.protocol === "https:" || u.protocol === "http:";
      } catch {
        return false;
      }
    }
    return manualUserMandatoryComplete;
  }, [
    canTriggerExecute,
    activeTask,
    manualUserMandatoryComplete,
    proposedConfig?.body,
    taskFormValues.instanceName,
    taskFormValues.subdomain,
    taskFormValues.baseUrl,
  ]);

  /** Mutating quick actions + inspector (requires SSWS write for this tab). */
  const canMutateTab = canApproveExecute;

  useEffect(() => {
    pendingInputBufferRef.current = pendingInputBuffer;
  }, [pendingInputBuffer]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    hiddenRef.current = hiddenSystemEvents;
  }, [hiddenSystemEvents]);

  useEffect(() => {
    selectedTreePathRef.current = selectedTreePath;
  }, [selectedTreePath]);

  const firstPathCleanupRef = useRef(true);
  useEffect(() => {
    cleanupRateLimitToReady();
    setPendingInputBuffer("");
    csvPendingRef.current = null;
    if (firstPathCleanupRef.current) {
      firstPathCleanupRef.current = false;
    } else {
      setOnboardingWizard(null);
      saveOnboardingWizardToStorage(null);
    }
    groupRulesAuditRawRef.current = null;
    setGroupRulesView({ status: "idle", rules: [], error: null, fetchedAt: null, progressSteps: null });
    setShowCsvButton(false);
    setActiveWorkflow(null);
  }, [selectedTreePath, cleanupRateLimitToReady]);

  useEffect(() => {
    memoryRef.current = conversationMemory;
  }, [conversationMemory]);

  /** After Okta executeAction — remember last app for SP/metadata; auto-complete roadmap for non-SSO wins */
  const onOktaExecutionResult = useCallback(
    (action, result, ok) => {
      if (!ok || !result) return;

      if (action?.type === "comprehensive_assessment") {
        const audit =
          result.audit && typeof result.audit === "object" ? result.audit : result;
        if (audit && typeof audit === "object" && typeof projectCtx?.setAuditData === "function") {
          projectCtx.setAuditData(audit);
        }
      }

      const app = result.app;
      if (app?.id) {
        setConversationMemory((prev) => ({
          ...prev,
          lastCreatedOktaApp: {
            id: String(app.id),
            label: app.label != null ? String(app.label) : "",
            signOnMode: app.signOnMode != null ? String(app.signOnMode) : "",
          },
        }));
      }

      const actionType = action?.type;
      const skipRoadmapAuto = new Set([
        "add_oin_app",
        "create_saml_app",
        "create_oidc_app",
        "create_swa_app",
        "assign_app_to_group",
        "create_and_assign_group",
        "get_app_metadata",
      ]);
      if (skipRoadmapAuto.has(actionType)) return;

      const pr = projectSyncRef.current.project;
      const upd = projectSyncRef.current.updateRoadmapItem;
      if (!pr?.roadmap?.items?.length || !upd) return;

      const items = pr.roadmap.items;
      const nowIso = new Date().toISOString();
      const isItemComplete = (it) => {
        const s = String(it?.status || "").toLowerCase();
        return s === "complete" || s === "completed" || it?.completed === true;
      };

      const patternsByType = {
        enable_threatinsight: ["threat", "threatinsight", "foundation"],
        update_password_policy: ["password", "policy"],
        enable_deprovisioning: ["deprovision", "lifecycle"],
        create_group: ["group"],
      };
      const patterns = patternsByType[actionType];
      if (!patterns) return;

      const match = items.find((it) => {
        if (!it || isItemComplete(it)) return false;
        const title = (it.title || "").toLowerCase();
        const id = (it.id || "").toLowerCase();
        return patterns.some((p) => title.includes(p) || id.includes(p));
      });
      if (!match?.id) return;

      void upd(match.id, {
        ...match,
        status: "complete",
        completed: true,
        completedAt: nowIso,
        notes: `Auto-completed after ${actionType}`,
      });
    },
    [setConversationMemory, projectCtx],
  );

  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  /** Sync SSWS-derived capabilities into agent memory for idwiz AI + UI. */
  useEffect(() => {
    setConversationMemory((prev) => ({
      ...prev,
      currentCapabilities: currentCapabilities ?? null,
    }));
  }, [currentCapabilities]);

  /** Silent: lifecycle automation eligibility (maps to okta.groups.manage / okta.policies.manage style checks via admin roles). */
  useEffect(() => {
    const la = currentCapabilities?.lifecycleAutomation;
    if (!la || (typeof la.oktaGroupsManage !== "boolean" && typeof la.oktaPoliciesManage !== "boolean")) {
      return;
    }
    setConversationMemory((prev) => {
      const prevLa = prev.lifecycleAutomation;
      if (
        prevLa &&
        prevLa.oktaGroupsManage === la.oktaGroupsManage &&
        prevLa.oktaPoliciesManage === la.oktaPoliciesManage
      ) {
        return prev;
      }
      return { ...prev, lifecycleAutomation: la };
    });
  }, [currentCapabilities?.lifecycleAutomation]);

  /** Keep Inspector JSON in sync with manual user form (Okta create user body). */
  useEffect(() => {
    if (activeTask !== "manual_user_create") return;
    setProposedConfig((pc) => {
      if (!pc || pc.format !== "json") return pc;
      const nextBody = JSON.stringify(buildOktaUserCreateBody(taskFormValues), null, 2);
      if (pc.body === nextBody) return pc;
      return { format: "json", body: nextBody };
    });
  }, [taskFormValues, activeTask]);

  /** When subdomain is set and base URL empty, default ServiceNow SaaS URL (still editable). */
  useEffect(() => {
    if (activeTask !== "oin_servicenow") return;
    const sub = (taskFormValues.subdomain || "").trim();
    if (!sub) return;
    setTaskFormValues((prev) => {
      const base = (prev.baseUrl || "").trim();
      if (base) return prev;
      return { ...prev, baseUrl: `https://${sub}.service-now.com` };
    });
  }, [taskFormValues.subdomain, activeTask]);

  const setTaskFormValue = useCallback((fieldId, value) => {
    setTaskFormValues((prev) => ({ ...prev, [fieldId]: value }));
  }, []);

  const appendMessage = useCallback((msg) => {
    setMessages((prev) => {
      const next = [...prev, { id: newId(), ...msg }];
      messagesRef.current = next;
      return next;
    });
  }, []);

  useEffect(() => {
    dailyLimitReachedRef.current = dailyLimitReached;
  }, [dailyLimitReached]);

  const persistDailyLimit = useCallback(() => {
    try {
      sessionStorage.setItem(DAILY_LIMIT_SESSION_KEY, "1");
    } catch {
      /* ignore */
    }
    dailyLimitReachedRef.current = true;
    setDailyLimitReached(true);
  }, []);

  /** Invoked from .catch when chat hits HTTP 429 or 503 RATE_LIMIT (backend limits). */
  const handleRateLimit429 = useCallback(
    (e, ctx = {}) => {
      setChatHttp429Active(true);
      const w = onboardingWizardRef.current;
      if (w?.step) {
        saveOnboardingWizardToStorage(w);
      }
      try {
        const snap = preserveAutomationRef.current;
        if (snap?.activeTask || snap?.activeFormFields?.length || snap?.proposedConfig?.body) {
          sessionStorage.setItem(
            "idwiz.automationPreserve",
            JSON.stringify({ ...snap, savedAt: Date.now() }),
          );
        }
      } catch {
        /* ignore */
      }

      const quotaMsg =
        mergeQuotaMessage(e?.data ?? {}) || String(e?.message ?? "");
      const kind = parseQuotaKind(quotaMsg);

      if (kind === "tpm") {
        setChatHttp429Active(false);
        setManualRetryPending(false);
        lastFailedChatAttemptRef.current = null;
        tpmThrottleUntilRef.current = Date.now() + TPM_SOFT_THROTTLE_WINDOW_MS;
        setRateLimitError("TPM_QUOTA");
        setPendingInputBuffer("");
        appendMessage({
          role: "assistant",
          kind: "error",
          content: "Message too large. Try a smaller request.",
          nextSteps: [],
          explainWhy: "",
          securityRecommendation: false,
        });
        return;
      }

      if (kind === "rpd") {
        setChatHttp429Active(false);
        setManualRetryPending(false);
        lastFailedChatAttemptRef.current = null;
        persistDailyLimit();
        setRateLimitError("RPD_QUOTA");
        setPendingInputBuffer("");
        appendMessage({
          role: "assistant",
          kind: "error",
          content:
            "Daily Limit Reached — idwiz AI is unavailable until you open a new browser session.",
          nextSteps: [],
          explainWhy: "",
          securityRecommendation: false,
        });
        return;
      }

      if (ctx.userText != null) {
        lastFailedChatAttemptRef.current = { userText: ctx.userText, mem: ctx.mem };
        setManualRetryPending(true);
      }
      setRateLimitError("RATE_LIMIT");
      setPendingInputBuffer("");
      blockContextIntroAfter429Ref.current = true;
    },
    [appendMessage, persistDailyLimit],
  );

  useEffect(() => {
    setDailyAiRequestCount(getDailyAiRequestState().count);
  }, []);

  useEffect(() => {
    const onAttempt = () => setDailyAiRequestCount(getDailyAiRequestState().count);
    window.addEventListener("idwiz-ai-attempt", onAttempt);
    return () => window.removeEventListener("idwiz-ai-attempt", onAttempt);
  }, []);

  const refreshDailyAiRequestCount = useCallback(() => {
    setDailyAiRequestCount(getDailyAiRequestState().count);
  }, []);

  useEffect(() => {
    if (onboardingWizard?.step) {
      saveOnboardingWizardToStorage(onboardingWizard);
    }
  }, [onboardingWizard]);

  useEffect(() => {
    const s = loadOnboardingWizardFromStorage();
    if (!s || s.treePath !== selectedTreePathRef.current) return;
    setOnboardingWizard((prev) => {
      if (prev) return prev;
      queueMicrotask(() => setActiveTask("onboarding_wizard"));
      return { step: s.step, answers: s.answers || {}, treePath: s.treePath };
    });
  }, []);

  /** When true, skip `runContextIntro` (avoids Welcome reset after HTTP 429). Cleared on successful chat or tab clear. */
  const blockContextIntroAfter429Ref = useRef(false);

  const prevQuotaSendLockedRef = useRef(false);
  useEffect(() => {
    if (prevQuotaSendLockedRef.current && !quotaSendLocked) {
      tryRestoreAutomationPreserve();
      if (onboardingWizardRef.current?.step) {
        appendMessage({
          role: "assistant",
          kind: "context_notice",
          content:
            "Rate limit ended — your onboarding answers are still here. Continue the wizard or submit the full plan when ready.",
        });
      }
      deferredContextIntroRef.current = false;
    }
    prevQuotaSendLockedRef.current = quotaSendLocked;
  }, [quotaSendLocked, tryRestoreAutomationPreserve, appendMessage]);

  const pushHiddenSystemEvent = useCallback((payload) => {
    const evt = {
      id: `sys-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      ts: Date.now(),
      ...payload,
    };
    setHiddenSystemEvents((prev) => {
      const next = [...prev, evt];
      hiddenRef.current = next;
      return next;
    });
    if (import.meta.env?.DEV) {
      // eslint-disable-next-line no-console
      console.debug("[idwiz] hidden system event → idwiz AI", evt);
    }
  }, []);

  const handleCsvFile = useCallback(
    async (file) => {
      if (!file || thinking) return;
      if (!showCsvButtonRef.current || activeWorkflowRef.current !== ACTIVE_WORKFLOW_CSV_IMPORT) {
        appendMessage({
          role: "assistant",
          kind: "error",
          content: "CSV import is available after you choose **Proceed with CSV Import** or **Automate onboarding** with source **CSV**.",
        });
        return;
      }
      if (!isCsvFile(file)) {
        appendMessage({
          role: "assistant",
          kind: "error",
          content: "Please drop a CSV file (.csv).",
        });
        return;
      }
      const label = file.name || "import.csv";
      try {
        const text = await readFileAsText(file);
        const { headers, rows } = parseCSV(text);
        if (!headers.length) {
          appendMessage({
            role: "assistant",
            kind: "error",
            content: "That file has no header row.",
          });
          return;
        }
        csvPendingRef.current = { headers, rows, fileName: label };
        appendMessage({
          role: "user",
          kind: "consultation",
          content: `[CSV dropped] ${label}`,
        });
        const headerList = headers.join(", ");
        appendMessage({
          role: "assistant",
          kind: "consultation",
          content: `I found ${headerList}. Should I map these to Okta's profile attributes?`,
        });
        setActiveOptionButtons([
          {
            id: "csv-profile-map-yes",
            label: "Yes, map to profile",
            description: "Build proposed user list for Okta",
          },
          {
            id: "csv-profile-map-no",
            label: "Not now",
            description: "Cancel this CSV import",
          },
        ]);
        pushHiddenSystemEvent({
          type: "csv_import_parsed",
          role: "system",
          hidden: true,
          instruction: `CSV parsed: ${label}, headers: ${JSON.stringify(headers)}, ${rows.length} data row(s).`,
        });
      } catch (e) {
        appendMessage({
          role: "assistant",
          kind: "error",
          content: `Could not read CSV: ${e?.message || "Unknown error"}`,
        });
      }
    },
    [appendMessage, thinking, pushHiddenSystemEvent],
  );

  const selectOnboardingSource = useCallback((value) => {
    if (value === "csv") {
      setShowCsvButton(true);
      setActiveWorkflow(ACTIVE_WORKFLOW_CSV_IMPORT);
      setActiveTask(ACTIVE_WORKFLOW_CSV_IMPORT);
    } else {
      setShowCsvButton(false);
      setActiveWorkflow(null);
      setActiveTask(null);
    }
    setOnboardingWizard((prev) => {
      if (!prev || prev.step !== 1) return prev;
      return {
        ...prev,
        step: 2,
        answers: { ...prev.answers, source: value },
      };
    });
  }, []);

  const updateOnboardingGroupsText = useCallback((text) => {
    setOnboardingWizard((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        answers: { ...prev.answers, groupsText: text },
      };
    });
  }, []);

  const continueOnboardingStep2 = useCallback(() => {
    setOnboardingWizard((prev) => {
      if (!prev || prev.step !== 2) return prev;
      return { ...prev, step: 3 };
    });
  }, []);

  const exitOnboardingWizard = useCallback(() => {
    setShowCsvButton(false);
    setActiveWorkflow(null);
    setOnboardingWizard(null);
    setActiveTask(null);
    saveOnboardingWizardToStorage(null);
    setActiveOptionButtons(getConciergeChips(pathToLeaf(selectedTreePathRef.current)));
  }, []);

  const dismissSuggestedActionSurface = useCallback(() => {
    if (thinking) return;
    const leaf = pathToLeaf(selectedTreePathRef.current);
    if (onboardingWizard) {
      exitOnboardingWizard();
      return;
    }
    if (activeTask === "csv_user_import" && !formActive) {
      csvPendingRef.current = null;
      setShowCsvButton(false);
      setActiveWorkflow(null);
      setActiveTask(null);
      setProposedConfig(null);
      setTaskFormValues({ ...EMPTY_TASK_FORM });
      setConversationMemory((m) => ({ ...m, consultationMode: true }));
      setActiveOptionButtons(getConciergeChips(leaf));
      return;
    }
    if (!formActive) return;
    setActiveFormFields(null);
    setArchitectPrimaryActionLabel(null);
    setActiveTask(null);
    setTaskFormValues({ ...EMPTY_TASK_FORM });
    setProposedConfig(null);
    setOinCatalogUi({ ...INITIAL_OIN_CATALOG_UI });
    setOinConfigureUi({ ...INITIAL_OIN_CONFIGURE });
    setGroupRulesView({ status: "idle", rules: [], error: null, fetchedAt: null, progressSteps: null });
    groupRulesAuditRawRef.current = null;
    setConversationMemory((m) => ({ ...m, consultationMode: true }));
    setActiveOptionButtons(getConciergeChips(leaf));
  }, [thinking, onboardingWizard, activeTask, formActive, exitOnboardingWizard, setArchitectPrimaryActionLabel]);

  useEffect(() => {
    if (!formActive) {
      setOinCatalogUi({ ...INITIAL_OIN_CATALOG_UI });
      setOinConfigureUi({ ...INITIAL_OIN_CONFIGURE });
    }
  }, [formActive]);

  const initialPathRef = useRef(true);
  const lastPathRef = useRef(selectedTreePath);

  useEffect(() => {
    const newCtx = pathToContext(selectedTreePath);
    setCurrentContext(newCtx);

    if (initialPathRef.current) {
      initialPathRef.current = false;
      lastPathRef.current = selectedTreePath;
      return;
    }

    if (lastPathRef.current === selectedTreePath) return;

    const prevPath = lastPathRef.current;
    lastPathRef.current = selectedTreePath;
    const prevCtx = pathToContext(prevPath);
    const mem = memoryRef.current;

    const instruction = buildSystemInstructionNav({
      prevCtx,
      newCtx,
      memory: mem,
      path: selectedTreePath,
    });

    pushHiddenSystemEvent({
      type: "idwiz_system_context",
      role: "system",
      hidden: true,
      instruction,
      previousPath: prevPath,
      currentPath: selectedTreePath,
      previousContext: prevCtx,
      currentContext: newCtx,
    });
  }, [selectedTreePath, pushHiddenSystemEvent]);

  /** Simulate idwiz AI when API unavailable. */
  const simulateAgentReply = useCallback(
    (userText, mem, phaseArg, selectedPath, interactionMode) => {
      const lower = userText.toLowerCase();
      let nextPhase = phaseArg;
      if (phaseArg === "gathering" && (lower.includes("policy") || lower.includes("mfa"))) {
        nextPhase = "recommending";
        setPhase("recommending");
        phaseRef.current = "recommending";
      }

      const isSecurity = nextPhase === "recommending";
      const ctx = pathToContext(selectedPath);
      const memHint = mem.projectName
        ? ` Context: ${ctx}. Active project: ${mem.projectName}.`
        : ` Context: ${ctx}.`;

      const leaf = pathToLeaf(selectedPath);
      const opts =
        interactionMode === "context_intro"
          ? getConciergeChips(leaf)
          : normalizeOptionButtons(
              null,
              ["Validate group membership", "Apply policy in sandbox", "Schedule cutover"],
            );

      if (interactionMode !== "context_intro") {
        appendMessage({
          role: "assistant",
          kind: "consultation",
          content:
            nextPhase === "recommending"
              ? "idwiz consultation: Based on your scope, I recommend tightening sign-on policy with phishing-resistant factors for admin groups. Review the proposed Okta JSON and Terraform on the right." + memHint
              : "idwiz consultation: I’ve captured your requirements. Next, confirm authenticator posture and API service scopes." + memHint,
          nextSteps: [],
          explainWhy: isSecurity
            ? "Phishing-resistant authenticators reduce account takeover for privileged Okta sessions (NIST SP 800-63B alignment)."
            : "Structured follow-ups keep policy changes traceable and reversible.",
          securityRecommendation: isSecurity,
        });
      }

      setActiveOptionButtons(opts);

      pushHiddenSystemEvent({
        type: "idwiz_turn_context",
        role: "system",
        hidden: true,
        instruction: `User message in context "${ctx}" with memory: ${JSON.stringify(mem)}. User said: ${userText.slice(0, 2000)}`,
      });

      if (interactionMode !== "context_intro" && nextPhase === "recommending") {
        if (lower.includes("terraform") || lower.includes("hcl")) {
          setProposedConfig({ format: "hcl", body: DEMO_PROPOSED_HCL });
          setConversationMemory((prev) => ({
            ...prev,
            consultationMode: false,
            proposedConfigDraft: { format: "hcl", body: DEMO_PROPOSED_HCL },
          }));
        } else if (lower.includes("policy") || lower.includes("mfa")) {
          setProposedConfig({ format: "json", body: DEMO_PROPOSED_JSON });
          setConversationMemory((prev) => ({
            ...prev,
            consultationMode: false,
            proposedConfigDraft: { format: "json", body: DEMO_PROPOSED_JSON },
          }));
        }
      }
    },
    [appendMessage, pushHiddenSystemEvent],
  );

  /**
   * Fetches group rules via SSWS (5s client timeout). Renders table in groupRulesView first; optional architect summary after when runSummaryAfter.
   * @param {{ announceMode?: "chip" | "agent", runSummaryAfter?: boolean }} [options]
   */
  const loadGroupRulesFromOkta = useCallback(
    async (options = {}) => {
      const { announceMode = "chip", runSummaryAfter = false } = options;
      const progressLoading = [
        { id: "fetch", label: "Fetching group rules via SSWS", state: "active" },
        { id: "render", label: "Rendering rules table", state: "pending" },
      ];
      setGroupRulesView((v) => ({ ...v, status: "loading", error: null, progressSteps: progressLoading }));
      try {
        const json = await listGroupRules({ timeoutMs: 5000 });
        const rules = mapActiveGroupRulesFromResponse(json?.rules);
        // Structured summary is fetched via a separate endpoint to keep Okta API access server-side.
        let structuredSummary = null;
        try {
          const sumRes = await fetchGroupRulesSummary();
          structuredSummary = sumRes?.summary ?? null;
        } catch {
          structuredSummary = null;
        }
        groupRulesAuditRawRef.current = { summary: structuredSummary, fetchedAt: Date.now() };
        const progressDone = [
          { id: "fetch", label: "Fetching group rules via SSWS", state: "done" },
          { id: "render", label: "Rendering rules table", state: "done" },
        ];
        setGroupRulesView({
          status: "success",
          rules,
          error: null,
          fetchedAt: Date.now(),
          progressSteps: progressDone,
        });
        const leaf = pathToLeaf(selectedTreePathRef.current);
        if (rules.length === 0) {
          appendMessage({
            role: "assistant",
            kind: "consultation",
            content: GROUP_RULES_EMPTY_MSG,
            nextSteps: [],
            explainWhy: "",
            securityRecommendation: false,
          });
        } else if (announceMode === "chip" && !runSummaryAfter) {
          appendMessage({
            role: "assistant",
            kind: "consultation",
            content: `I am now displaying ${rules.length} active group rule(s) in the table.`,
            nextSteps: [],
            explainWhy: "",
            securityRecommendation: false,
          });
        }
        if (runSummaryAfter && rules.length > 0) {
          const summaryUserText = buildGroupRulesAuditUserText(
            structuredSummary || { error: "summary_unavailable", active_rules_count: rules.length },
            rules.length,
          );
          queueMicrotask(() => {
            runAgentRef.current?.(summaryUserText, memoryRef.current, messagesRef.current);
          });
        }
        setActiveOptionButtons(getConciergeChips(leaf));
      } catch (e) {
        const isAbort = e?.name === "AbortError" || e?.message?.includes?.("aborted");
        const msg = isAbort
          ? "Could not load group rules in time (5s). Check your connection and SSWS token, then try again."
          : e?.message || "Failed to load group rules from Okta.";
        groupRulesAuditRawRef.current = null;
        setGroupRulesView({
          status: "error",
          rules: [],
          error: msg,
          fetchedAt: Date.now(),
          progressSteps: null,
        });
        appendMessage({
          role: "assistant",
          kind: "error",
          content: `idwiz consultation: ${msg}`,
          nextSteps: [],
          explainWhy: "",
          securityRecommendation: false,
        });
        setActiveOptionButtons(getConciergeChips(pathToLeaf(selectedTreePathRef.current)));
      }
    },
    [appendMessage],
  );

  const applySuccessfulAgentResponse = useCallback(
    async (data, userText, mem, options = {}) => {
      blockContextIntroAfter429Ref.current = false;
      const { skipAssistantMessage = false, fallbackChipsLeaf = null } = options;
      const hasExec = Boolean(data.executeAction?.type);
      const ctx = pathToContext(selectedTreePathRef.current);
      const lower = userText.toLowerCase();
      const isGroupRulesAuditTurn = String(userText || "").includes(GROUP_RULES_AUDIT_PROMPT_TAG);
      if (
        !isGroupRulesAuditTurn &&
        phaseRef.current === "gathering" &&
        (lower.includes("policy") || lower.includes("mfa"))
      ) {
        setPhase("recommending");
        phaseRef.current = "recommending";
      }

      const rawAgentMsg = String(data.message ?? data.text ?? "").trim();
      const phraseGroupRules = /reviewing\s+group\s+rules/i.test(rawAgentMsg);
      const suppressConsultForGroupRulesUi =
        !isGroupRulesAuditTurn &&
        (data.activeTask === "review_group_rules" || phraseGroupRules);
      const isAuthFactorsAuditTurn = String(userText || "").includes(AUTH_ACTIVE_FACTORS_AUDIT_TAG);

      const archBlock = formatArchitectureSteps(data.architectureSteps);
      let text = rawAgentMsg;
      if (isAuthFactorsAuditTurn) {
        text = text
          .replace(/^idwiz\s+consultation:\s*/i, "")
          .replace(/\bidwiz\s+consultation:\s*/gi, "\n")
          .replace(/^I am now preparing[^\n]*\n?/i, "")
          .replace(/^I am now preparing[^\n.]*\.\s*/i, "")
          .trim();
      } else {
        if (archBlock) {
          text = text ? `${archBlock}\n\n${text}` : archBlock;
        }
        if (text && !/^(idwiz|architect) consultation:/i.test(text)) {
          text = `idwiz consultation: ${text}`;
        }
      }

      const explainWhy = data.explainWhy != null ? String(data.explainWhy) : "";
      const nextSteps = Array.isArray(data.nextSteps) ? data.nextSteps.map(String) : [];
      let buttons = normalizeOptionButtons(data.optionButtons, nextSteps);
      if (!buttons.length && fallbackChipsLeaf) {
        buttons = getConciergeChips(fallbackChipsLeaf);
      }

      if (!skipAssistantMessage && !hasExec && text && !suppressConsultForGroupRulesUi) {
        appendMessage({
          role: "assistant",
          kind: isGroupRulesAuditTurn ? "group_rules_audit" : "consultation",
          content: text,
          nextSteps,
          explainWhy,
          securityRecommendation: Boolean(data.securityRecommendation),
          architectureSteps: Array.isArray(data.architectureSteps) ? data.architectureSteps : [],
        });
      }

      setActiveOptionButtons(buttons);

      pushHiddenSystemEvent({
        type: "idwiz_turn_context",
        role: "system",
        hidden: true,
        instruction: `idwiz AI response for context "${ctx}"; memory: ${JSON.stringify(mem)}`,
      });

      const revealTenant = data.revealTenantInputs === true;
      const fromApi = normalizeProposedConfig(data.proposedConfig);

      const ff = Array.isArray(data.formFields) ? data.formFields : [];
      let at = null;
      if (data.activeTask === "manual_user_create") at = "manual_user_create";
      else if (data.activeTask === "ad_integration") at = "ad_integration";
      else if (data.activeTask === "review_group_rules") at = "review_group_rules";
      else if (data.activeTask === "oin_servicenow") at = "oin_servicenow";
      else if (data.activeTask === "OIN_SEARCH") at = "OIN_SEARCH";
      else if (data.activeTask === "CUSTOM_SAML") at = "CUSTOM_SAML";

      if (phraseGroupRules && at !== "manual_user_create" && at !== "ad_integration") {
        at = "review_group_rules";
      }

      if (ff.length > 0) {
        setActiveFormFields(ff);
        setActiveTask(at === "manual_user_create" ? "manual_user_create" : at);
        if (at === "manual_user_create" && fromApi) {
          setTaskFormValues({ ...EMPTY_TASK_FORM, ...parseProfileFromProposedBody(fromApi.body) });
        } else {
          setTaskFormValues((prev) => {
            const next = { ...prev };
            for (const f of ff) {
              const fid = f.id != null ? String(f.id) : "";
              if (fid && next[fid] === undefined) next[fid] = "";
            }
            if (at === "oin_servicenow") {
              const hint = extractServiceNowHints(messagesRef.current, mem);
              if (!String(next.subdomain || "").trim() && hint.subdomain) next.subdomain = hint.subdomain;
              if (!String(next.baseUrl || "").trim()) {
                next.baseUrl = hint.baseUrl || (hint.subdomain ? `https://${hint.subdomain}.service-now.com` : "");
              }
            }
            return next;
          });
        }
      } else {
        setActiveFormFields(null);
        if (at === "ad_integration") {
          setActiveTask("ad_integration");
        } else if (at === "review_group_rules") {
          setActiveTask("review_group_rules");
        } else {
          setActiveTask(null);
        }
        setTaskFormValues({ ...EMPTY_TASK_FORM });
      }

      if (fromApi && at !== "oin_servicenow") {
        setProposedConfig((prev) => mergeProposedConfigIncremental(prev, fromApi));
        setConversationMemory((prev) => {
          const merged = mergeProposedConfigIncremental(
            prev.proposedConfigDraft ? normalizeProposedConfig(prev.proposedConfigDraft) : null,
            fromApi,
          );
          return {
            ...prev,
            consultationMode: false,
            proposedConfigDraft: merged || prev.proposedConfigDraft,
          };
        });
      } else if (revealTenant) {
        setConversationMemory((prev) => ({ ...prev, consultationMode: false }));
      }

      if (at === "oin_servicenow" && ff.length > 0) {
        setConversationMemory((prev) => ({ ...prev, consultationMode: false }));
      }

      const shouldLoadGroupRules =
        data.activeTask === "review_group_rules" ||
        at === "review_group_rules" ||
        (phraseGroupRules && at !== "manual_user_create" && at !== "ad_integration");
      /** Avoid chaining a second fetch/audit while applying the response to the audit prompt itself. */
      if (shouldLoadGroupRules && !isGroupRulesAuditTurn) {
        setActiveTask("review_group_rules");
        const snap = groupRulesViewRef.current;
        const raw = groupRulesAuditRawRef.current;
        const canReuseSession =
          snap?.status === "success" &&
          Array.isArray(snap.rules) &&
          snap.rules.length > 0 &&
          raw?.summary != null;
        if (canReuseSession) {
          queueMicrotask(() => {
            const summaryUserText = buildGroupRulesAuditUserText(raw.summary, snap.rules.length);
            runAgentRef.current?.(summaryUserText, memoryRef.current, messagesRef.current);
          });
        } else {
          setGroupRulesView({
            status: "loading",
            rules: [],
            error: null,
            fetchedAt: null,
            progressSteps: [
              { id: "fetch", label: "Fetching group rules via SSWS", state: "active" },
              { id: "render", label: "Rendering rules table", state: "pending" },
            ],
          });
          queueMicrotask(() =>
            loadGroupRulesFromOkta({ announceMode: "agent", runSummaryAfter: true }),
          );
        }
      }

      const ru = data.roadmapUpdate;
      if (
        ru &&
        projectSyncRef.current.project?.projectId &&
        !isAuth0StubProject(projectSyncRef.current.project)
      ) {
        if (!ru.item?.id) {
          /* skip invalid roadmap payloads */
        } else {
          const { action, item } = ru;
          const { addRoadmapItem: addR, updateRoadmapItem: updR } = projectSyncRef.current;
          queueMicrotask(async () => {
            try {
              if (action === "add" && addR) {
                const pr = projectSyncRef.current.project;
                const items = pr?.roadmap?.items;
                const existingByTitle =
                  Array.isArray(items) && item?.title
                    ? items.find(
                        (i) =>
                          i &&
                          String(i.title).trim().toLowerCase() === String(item.title).trim().toLowerCase() &&
                          String(i.id) !== String(item.id),
                      )
                    : null;
                if (existingByTitle && updR) {
                  await updR(existingByTitle.id, { ...item, id: existingByTitle.id });
                } else {
                  await addR(item);
                }
              } else if (updR) await updR(item.id, item);
            } catch (e) {
              console.warn("[useIdwiz] roadmap sync (agent)", e);
            }
          });
        }
      }
      const mu = data.memoryUpdate;
      if (mu && projectSyncRef.current.project?.projectId && projectSyncRef.current.syncProject) {
        const pr = projectSyncRef.current.project;
        const sync = projectSyncRef.current.syncProject;
        queueMicrotask(async () => {
          try {
            await sync({
              discoveryAnswers: {
                ...(pr.discoveryAnswers && typeof pr.discoveryAnswers === "object" ? pr.discoveryAnswers : {}),
                ...(mu.discoveryAnswers && typeof mu.discoveryAnswers === "object" ? mu.discoveryAnswers : {}),
              },
              businessContext: {
                ...(pr.businessContext && typeof pr.businessContext === "object" ? pr.businessContext : {}),
                ...(mu.businessContext && typeof mu.businessContext === "object" ? mu.businessContext : {}),
              },
              discoveryComplete: mu.discoveryComplete ?? pr.discoveryComplete,
              discoveryProgress: mu.discoveryProgress ?? pr.discoveryProgress,
            });
          } catch (e) {
            console.warn("[useIdwiz] memory sync (agent)", e);
          }
        });
      }

      if (data.executeAction?.type) {
        await orchestrateExecuteAction({
          action: data.executeAction,
          project: projectSyncRef.current.project,
          setMessages,
          sendMessage: (text, o) => architectSendMessage(text, o),
          onExecutionResult: onOktaExecutionResult,
          refreshProject: loadProjectFromCtx,
        });
      }
    },
    [
      appendMessage,
      loadGroupRulesFromOkta,
      pushHiddenSystemEvent,
      architectSendMessage,
      setMessages,
      onOktaExecutionResult,
    ],
  );

  const fetchAgent = useCallback(
    async (userText, mem, history, interactionMode) => {
      const ctx = pathToContext(selectedTreePathRef.current);
      const at = activeTaskRef.current;
      const oinQueryTrimmed =
        at === "OIN_SEARCH" ? String(taskFormValuesRef.current?.oinQuery ?? "").trim() : "";

      const pr = projectSyncRef.current.project;
      const aud = auditDataRef.current ?? pr?.auditSnapshot ?? null;
      const dr = driftDataRef.current;
      const hist = (history || []).filter((m) => m.hidden !== true);
      return architectSendMessage("", {
        rawPayload: {
          userMessage: userText,
          messages: hist.map((m) => ({
            role: m.role,
            content: typeof m.content === "string" ? m.content : JSON.stringify(m.content ?? ""),
            kind: m.kind,
          })),
          currentContext: ctx,
          selectedTreePath: selectedTreePathRef.current,
          conversationMemory: mem,
          hiddenSystemEvents: hiddenRef.current.slice(-35),
          phase: phaseRef.current,
          interactionMode,
          activeTask: at,
          ...(at === "OIN_SEARCH" && oinQueryTrimmed
            ? { oinQuery: oinQueryTrimmed, q: oinQueryTrimmed }
            : {}),
          okta:
            oktaConnected && oktaOrgUrl && oktaApiToken
              ? { orgUrl: oktaOrgUrl, apiToken: oktaApiToken }
              : null,
          project: pr ?? null,
          auditFindings: aud?.findings ?? null,
          driftChanges: Array.isArray(dr?.changes) ? dr.changes : null,
        },
      });
    },
    [oktaConnected, oktaOrgUrl, oktaApiToken, architectSendMessage],
  );

  /** Tab switch: static greeting from STATIC_TAB_GREETING + local chips — no architect fetch. */
  const runContextIntro = useCallback(() => {
    if (blockContextIntroAfter429Ref.current) {
      return;
    }
    const at = activeTaskRef.current;
    if (at === "OIN_SEARCH" || at === "oin_servicenow" || at === "CUSTOM_SAML") {
      return;
    }
    const path = selectedTreePathRef.current;
    const leaf = pathToLeaf(path);

    setActiveFormFields(null);
    setActiveTask(null);
    setTaskFormValues({ ...EMPTY_TASK_FORM });
    setGroupRulesView({ status: "idle", rules: [], error: null, fetchedAt: null, progressSteps: null });
    groupRulesAuditRawRef.current = null;

    if (skipNextContextIntroRef.current) {
      skipNextContextIntroRef.current = false;
      return;
    }

    setCommandIntentLabel(null);

    const welcomeText = STATIC_TAB_GREETING[path] ?? getConciergeWelcome(leaf);
    const welcomeMsg = {
      id: newId(),
      role: "assistant",
      kind: "consultation",
      staticTabIntro: true,
      content: welcomeText,
      nextSteps: [],
      explainWhy: "",
      securityRecommendation: false,
    };
    setMessages((prev) => {
      const next = [...prev, welcomeMsg];
      messagesRef.current = next;
      return next;
    });
    setActiveOptionButtons(getConciergeChips(leaf));
  }, []);

  useEffect(() => {
    if (quotaSendLocked) {
      deferredContextIntroRef.current = true;
      return;
    }
    deferredContextIntroRef.current = false;

    const path = selectedTreePath;
    const pathChanged = pathForIntroEffectRef.current !== path;

    if (isFirstPathIntroEffectRef.current) {
      isFirstPathIntroEffectRef.current = false;
      pathForIntroEffectRef.current = path;
      if (project?.projectId) {
        return;
      }
      runContextIntro();
      return;
    }

    if (pathChanged) {
      pathForIntroEffectRef.current = path;
      runContextIntro();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- path / quota lock
  }, [selectedTreePath, quotaSendLocked, runContextIntro, project?.projectId]);

  const runAgent = useCallback(
    async (userText, memoryOverride, historyOverride, interactionMode = "chat") => {
      if (dailyLimitReachedRef.current) {
        appendMessage({
          role: "assistant",
          kind: "error",
          content: "Daily Limit Reached — idwiz AI is disabled for this browser session.",
          nextSteps: [],
          explainWhy: "",
          securityRecommendation: false,
        });
        return;
      }
      const mem = memoryOverride ?? conversationMemory;
      const history = historyOverride ?? messagesRef.current;
      const seq = ++agentSeqRef.current;
      lastAgentUserTextRef.current = String(userText || "");

      setThinking(true);
      setConversationMemory((m) => ({ ...m, agentMode: "CONSULTATION" }));
      try {
        let result;
        try {
          result = await fetchAgent(userText, mem, history, interactionMode);
        } catch (e) {
          if (seq !== agentSeqRef.current) return;
          const isRateLimit =
            e?.code === "rate_limit" ||
            e?.code === "RATE_LIMIT" ||
            e?.status === 429 ||
            (e?.status === 503 &&
              (e?.data?.error === "RATE_LIMIT" || e?.data?.error === "rate_limit"));
          if (isRateLimit) {
            if (e?.retryAfter != null) triggerRateLimit(e.retryAfter);
            handleRateLimit429(e, { kind: "chat", userText, mem, history });
            return;
          }
          setChatHttp429Active(false);
          const hint =
            typeof e?.message === "string" && e.message.trim() && e.message.length < 220
              ? ` (${e.message.trim()})`
              : "";
          appendMessage({
            role: "assistant",
            kind: "error",
            content: `idwiz consultation: The request could not be completed${hint}. Please try again. For the printable CISO report, use “Full audit report” above.`,
            nextSteps: [],
            explainWhy: "",
            securityRecommendation: false,
          });
          return;
        }

        if (seq !== agentSeqRef.current) return;

        const data = result;
        setRateLimitError(null);
        setChatHttp429Active(false);
        clearRateLimitPadding();

        if (!data?.ok) {
          if (data?.error === "not_configured") {
            simulateAgentReply(userText, mem, phaseRef.current, selectedTreePathRef.current, interactionMode);
            return;
          }
          appendMessage({
            role: "assistant",
            kind: "error",
            content: `idwiz consultation: ${data?.message || data?.error || "Request could not be completed."}`,
            nextSteps: [],
            explainWhy: "",
            securityRecommendation: false,
          });
          return;
        }

        lastFailedChatAttemptRef.current = null;
        setManualRetryPending(false);
        await applySuccessfulAgentResponse(data, userText, mem);
        setRateLimitError(null);
        setChatHttp429Active(false);
      } finally {
        if (seq === agentSeqRef.current) setThinking(false);
      }
    },
    [
      appendMessage,
      applySuccessfulAgentResponse,
      clearRateLimitPadding,
      conversationMemory,
      fetchAgent,
      handleRateLimit429,
      simulateAgentReply,
      setActiveOptionButtons,
      triggerRateLimit,
    ],
  );

  useEffect(() => {
    runAgentRef.current = runAgent;
  }, [runAgent]);

  /** After hard-reset reload, run the pending chat attempt once (in-memory quota state was cleared). */
  useEffect(() => {
    let cancelled = false;
    const timer = window.setTimeout(() => {
      if (cancelled) return;
      try {
        const raw = sessionStorage.getItem(HARD_RESET_CHAT_RETRY_KEY);
        if (!raw) return;
        sessionStorage.removeItem(HARD_RESET_CHAT_RETRY_KEY);
        const parsed = JSON.parse(raw);
        if (!parsed?.userText || typeof parsed.userText !== "string") return;
        if (Date.now() - (parsed.at || 0) > 5 * 60 * 1000) return;
        const mem = parsed.mem && typeof parsed.mem === "object" ? parsed.mem : memoryRef.current;
        runAgentRef.current?.(parsed.userText, mem, messagesRef.current);
      } catch {
        try {
          sessionStorage.removeItem(HARD_RESET_CHAT_RETRY_KEY);
        } catch {
          /* ignore */
        }
      }
    }, 0);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, []);

  const submitOnboardingWizardPlan = useCallback(async () => {
    const w = onboardingWizardRef.current;
    if (!w || w.step !== 3) return;
    releaseRateLimitGate();
    if (thinking) return;
    const prompt = buildOnboardingSubmitPrompt(w.answers);
    const mem = memoryRef.current;
    const ctx = pathToContext(selectedTreePathRef.current);
    const withAnswer = {
      ...mem,
      discoveryAnswers: [
        ...(mem.discoveryAnswers || []),
        {
          type: "onboarding_wizard_submit",
          context: ctx,
          at: Date.now(),
        },
      ],
    };
    setConversationMemory(withAnswer);
    setOnboardingWizard(null);
    setActiveTask(null);
    saveOnboardingWizardToStorage(null);
    setActiveOptionButtons(getConciergeChips(pathToLeaf(selectedTreePathRef.current)));

    const userMsg = {
      id: newId(),
      role: "user",
      kind: "consultation",
      content: "[Submitted onboarding plan]",
    };
    pushHiddenSystemEvent({
      type: "idwiz_memory_update",
      role: "system",
      hidden: true,
      instruction: `Onboarding wizard completed; submitting consolidated plan to idwiz AI.`,
    });
    setMessages((prev) => {
      const next = [...prev, userMsg];
      messagesRef.current = next;
      queueMicrotask(() => runAgent(prompt, withAnswer, next));
      return next;
    });
  }, [thinking, pushHiddenSystemEvent, releaseRateLimitGate, runAgent]);

  const submitUserMessage = useCallback(
    async (text, options = {}) => {
      const {
        flushAfterRetry = false,
        interactionMode: submitInteractionMode = "chat",
        executeIntent = false,
      } = options;
      if (thinking && !flushAfterRetry) return;
      if (dailyLimitReachedRef.current) return;
      const t = String(text || "").trim();
      if (!t) return;
      resetInactivityTimer();
      lastFailedChatAttemptRef.current = null;
      setManualRetryPending(false);

      const isAppsFirst = detectAppsFirstIntent(t);
      if (isAppsFirst && !oktaConnected) {
        appendMessage({
          role: "assistant",
          kind: "error",
          content: CONNECT_OKTA_FIRST_MSG,
          nextSteps: [],
          explainWhy: "",
          securityRecommendation: false,
        });
        return;
      }
      if (!oktaConnected && /\bconfigure\s+(?:an?\s+)?application\b/i.test(t)) {
        appendMessage({
          role: "assistant",
          kind: "error",
          content: CONNECT_OKTA_FIRST_MSG,
          nextSteps: [],
          explainWhy: "",
          securityRecommendation: false,
        });
        return;
      }

      if (isAppsFirst) {
        skipNextContextIntroRef.current = true;
        selectedTreePathRef.current = APPS_FIRST_DEFAULT_PATH;
        setSelectedTreePath(APPS_FIRST_DEFAULT_PATH);
        setCommandIntentLabel(APPS_FIRST_INTENT_HEADLINE);
        setActiveOptionButtons([...APPS_FIRST_INTENT_CHIPS]);
      }

      const ctx = pathToContext(selectedTreePathRef.current);
      const updatedMemory = ingestMemoryFromUserText(t, conversationMemory);
      const wantsExecutionFromFreeText = executeIntent || isWriteIntent(t);
      const withAnswer = {
        ...updatedMemory,
        consultationMode: wantsExecutionFromFreeText ? false : updatedMemory.consultationMode,
        discoveryAnswers: [
          ...updatedMemory.discoveryAnswers,
          { type: "freeform", answer: t, context: ctx, at: Date.now() },
        ],
      };
      setConversationMemory(withAnswer);

      const capsForApps = currentCapabilities ?? memoryRef.current?.currentCapabilities;
      const appIntegrationWrite = hasApplicationIntegrationWrite(capsForApps);

      if (isAppsFirst) {
        pushHiddenSystemEvent({
          type: "idwiz_user_intent_pivot",
          role: "system",
          hidden: true,
          instruction: `User prioritized Applications workflow. Navigated to ${APPS_FIRST_DEFAULT_PATH}. SSWS app-integration write (OIDC/SAML/Apps tab): ${appIntegrationWrite}.`,
        });
      }

      const userMsg = {
        id: newId(),
        role: "user",
        kind: "consultation",
        content: t,
      };

      pushHiddenSystemEvent({
        type: "idwiz_memory_update",
        role: "system",
        hidden: true,
        instruction: `Updated conversation memory after user message: ${JSON.stringify(withAnswer)}`,
      });

      const textForAgent = isAppsFirst
        ? `${t}\n\n[PIVOT: User chose to start with Applications / app integrations ahead of other work. Validated SSWS allows app administration on this tenant: ${appIntegrationWrite}. Do not redirect them to Users or a linear checklist — pivot immediately to OIDC, SAML, or app catalog guidance. Refresh optionButtons for app integration tasks when appropriate.]`
        : t;

      setMessages((prev) => {
        const next = [...prev, userMsg];
        messagesRef.current = next;
        queueMicrotask(async () => {
          let agentText = textForAgent;
          if (wantsActiveFactorsAudit(t, ctx) && oktaConnected) {
            try {
              const authRes = await fetchAuthenticatorsSummary();
              const summary = authRes?.summary ?? null;
              pushHiddenSystemEvent({
                type: "okta_authenticators_active",
                role: "system",
                hidden: true,
                instruction: `Loaded authenticator structured summary via server context endpoint; model summarizes with security notes.`,
              });
              agentText = `${AUTH_ACTIVE_FACTORS_AUDIT_TAG}
User question: ${t}

Structured summary from GET {org}/api/v1/authenticators?status=ACTIVE:
${JSON.stringify(summary, null, 2)}

Summarize these active authenticators for the user. Flag weaker factors such as SMS, voice, and email one-time passcodes as elevated phishing/interception risk where applicable. Prefer phishing-resistant methods (WebAuthn/FIDO2; Okta Verify with user presence) in recommendations.
Output requirement: lead with substantive findings — no filler such as "I am now preparing", "idwiz consultation", or generic "I am now…" delay phrases.`;
            } catch (e) {
              appendMessage({
                role: "assistant",
                kind: "error",
                content: String(e?.message || "Could not load active authenticators from Okta."),
                nextSteps: [],
                explainWhy: "",
                securityRecommendation: false,
              });
              return;
            }
          }
          if (isAppsFirst && !appIntegrationWrite) {
            appendMessage({
              role: "assistant",
              kind: "context_notice",
              content:
                "To create or change app integrations, validate SSWS in the tenant panel with Application Administrator, API Access Management Administrator, or Organization/Super Administrator. Suggested actions are ready; idwiz AI will align once the token has app write scope.",
            });
          }
          runAgent(agentText, withAnswer, messagesRef.current, submitInteractionMode);
        });
        return next;
      });
      setInput("");
      setPendingInputBuffer("");
    },
    [
      appendMessage,
      conversationMemory,
      currentCapabilities,
      pushHiddenSystemEvent,
      releaseRateLimitGate,
      runAgent,
      thinking,
      oktaConnected,
      resetInactivityTimer,
    ],
  );

  const handleInputChange = useCallback((e) => {
    setInput(e.target.value);
  }, []);

  /** Hidden bypass: same hard reset as Retry — clears in-memory quota lock, then full reload + resume. */
  const forceQuotaRetryNow = useCallback(() => {
    forceClearSendLock();
    setChatHttp429Active(false);
    setRateLimitError(null);
    const p = lastFailedChatAttemptRef.current;
    if (!p?.userText) {
      window.location.reload();
      return;
    }
    try {
      sessionStorage.setItem(
        HARD_RESET_CHAT_RETRY_KEY,
        JSON.stringify({
          userText: p.userText,
          mem: p.mem,
          at: Date.now(),
        }),
      );
    } catch {
      try {
        sessionStorage.setItem(
          HARD_RESET_CHAT_RETRY_KEY,
          JSON.stringify({ userText: p.userText, at: Date.now() }),
        );
      } catch {
        /* ignore */
      }
    }
    window.location.reload();
  }, [forceClearSendLock]);

  const sendMessage = useCallback(async (opts = {}) => {
    const p = lastFailedChatAttemptRef.current;
    if (manualRetryPending && p && !input.trim()) {
      setRateLimitError(null);
      releaseRateLimitGate();
      try {
        sessionStorage.setItem(
          HARD_RESET_CHAT_RETRY_KEY,
          JSON.stringify({
            userText: p.userText,
            mem: p.mem,
            at: Date.now(),
          }),
        );
      } catch {
        try {
          sessionStorage.setItem(
            HARD_RESET_CHAT_RETRY_KEY,
            JSON.stringify({ userText: p.userText, at: Date.now() }),
          );
        } catch {
          /* ignore */
        }
      }
      window.location.reload();
      return;
    }
    setRateLimitError(null);
    releaseRateLimitGate();
    await submitUserMessage(input, { executeIntent: Boolean(opts.executeIntent) });
  }, [input, submitUserMessage, releaseRateLimitGate, manualRetryPending, runAgent]);

  const applyNextStep = useCallback(
    (step) => {
      setRateLimitError(null);
      releaseRateLimitGate();
      if (thinking) return;
      const opt =
        typeof step === "string"
          ? { id: `legacy-${String(step).slice(0, 24)}`, label: step, description: "" }
          : step;
      if (opt.id === IDWIZ_REGENERATE_ANALYSIS_ID) {
        const prompt = lastAgentUserTextRef.current;
        if (!prompt) return;
        queueMicrotask(() => runAgent(prompt, memoryRef.current, messagesRef.current));
        return;
      }
      if (opt.id === "mark-roadmap-complete") {
        const pr = projectSyncRef.current.project;
        const upd = projectSyncRef.current.updateRoadmapItem;
        const items = pr?.roadmap?.items;
        const inProgress = Array.isArray(items)
          ? items.find((i) => String(i?.status || "").toLowerCase().replace(/\s+/g, "_") === "in_progress")
          : null;
        if (inProgress && upd) {
          void upd(inProgress.id, {
            ...inProgress,
            status: "complete",
            completed: true,
            completedAt: new Date().toISOString(),
          });
        }
        void submitUserMessage(
          "[User confirmed task complete — mark roadmap item done and suggest next steps]",
          { interactionMode: "chat" },
        );
        return;
      }
      if (opt.id === "mark-salesforce-sso-complete" || opt.id === "sso-is-working") {
        const pr = projectSyncRef.current.project;
        const upd = projectSyncRef.current.updateRoadmapItem;
        const items = pr?.roadmap?.items || [];
        const item =
          items.find((i) => {
            const t = (i.title || "").toLowerCase();
            const id = (i.id || "").toLowerCase();
            const st = String(i?.status || "").toLowerCase();
            return (
              st !== "complete" &&
              st !== "completed" &&
              (t.includes("salesforce") || id.includes("salesforce") || t.includes("sso") || t.includes("saml"))
            );
          }) ||
          items.find((i) => String(i?.status || "").toLowerCase().replace(/\s+/g, "_") === "in_progress");
        if (item?.id && upd) {
          void upd(item.id, {
            ...item,
            status: "complete",
            completed: true,
            completedAt: new Date().toISOString(),
            notes: "User confirmed SP-side SSO is working",
          });
        }
        void submitUserMessage(
          "[User confirmed Salesforce / SAML SSO is working on the service-provider side — congratulate briefly and suggest the next roadmap item.]",
          { interactionMode: "chat" },
        );
        return;
      }
      if (opt.id === "show-saml-metadata") {
        const mem = memoryRef.current?.lastCreatedOktaApp;
        if (!mem?.id) {
          void submitUserMessage(
            "[PIVOT: User asked for Okta SAML metadata URLs. Use the most recently created app in this session or ask which application.]",
            { interactionMode: "chat" },
          );
          return;
        }
        void orchestrateExecuteAction({
          action: { type: "get_app_metadata", params: { appId: mem.id } },
          project: projectSyncRef.current.project,
          setMessages,
          sendMessage: (text, o) => architectSendMessage(text, o),
          onExecutionResult: onOktaExecutionResult,
          refreshProject: loadProjectFromCtx,
        });
        return;
      }
      if (opt.id === "help-salesforce-sp-config") {
        void submitUserMessage(
          "[PIVOT: User wants step-by-step Salesforce SP-side SAML SSO configuration. Respond with architectureSteps (kind requirement) and plain-English copy-paste guidance.]",
          { interactionMode: "chat" },
        );
        return;
      }
      if (!oktaConnected && optionRequiresOktaTenant(opt.id)) {
        window.alert("Please connect Okta to continue");
        return;
      }
      const ctx = pathToContext(selectedTreePathRef.current);
      const entry = {
        type: "option",
        optionId: opt.id ?? null,
        label: opt.label ?? String(step),
        answer: opt.label ?? String(step),
        context: ctx,
        at: Date.now(),
      };
      const prev = memoryRef.current;
      let updatedMemory = {
        ...prev,
        discoveryAnswers: [...(prev.discoveryAnswers || []), entry],
        sswsInterview: { ...(prev.sswsInterview || { readOnlyTerraformMode: false }) },
      };
      if (opt.id === "mode-readonly-terraform") {
        updatedMemory.sswsInterview = {
          ...updatedMemory.sswsInterview,
          readOnlyTerraformMode: true,
        };
      }
      if (
        opt.id === "integrate-with-ad" ||
        opt.id === "intent-browse-app-catalog" ||
        opt.id === "configure-sso" ||
        opt.id === "intent-create-oidc-app" ||
        opt.id === "intent-configure-saml"
      ) {
        updatedMemory = { ...updatedMemory, consultationMode: false };
      }
      setConversationMemory(updatedMemory);

      const display = opt.label ?? String(step);
      const userMsg = {
        id: newId(),
        role: "user",
        kind: "consultation",
        content: `[Selected] ${display}`,
      };

      pushHiddenSystemEvent({
        type: "idwiz_memory_update",
        role: "system",
        hidden: true,
        instruction: `Discovery option selected: ${JSON.stringify(entry)}`,
      });

      if (opt.id === "csv-import-proceed") {
        setShowCsvButton(true);
        setActiveWorkflow(ACTIVE_WORKFLOW_CSV_IMPORT);
        setActiveTask(ACTIVE_WORKFLOW_CSV_IMPORT);
        setMessages((prev) => {
          const next = [...prev, userMsg];
          messagesRef.current = next;
          return next;
        });
        appendMessage({
          role: "assistant",
          kind: "consultation",
          content:
            "Drop a **.csv** on the command area or use **Choose CSV** below. After the file loads, you can map columns and confirm import.",
          nextSteps: [],
          explainWhy: "",
          securityRecommendation: false,
        });
        setActiveOptionButtons(getConciergeChips(pathToLeaf(selectedTreePathRef.current)));
        return;
      }

      if (
        opt.id !== "csv-profile-map-yes" &&
        opt.id !== "csv-profile-map-no" &&
        opt.id !== "intent-browse-app-catalog"
      ) {
        setShowCsvButton(false);
        setActiveWorkflow(null);
        setActiveTask(null);
      }

      if (opt.id === ONBOARDING_WIZARD_START_OPTION_ID) {
        const treePath = selectedTreePathRef.current;
        setMessages((prev) => {
          const next = [...prev, userMsg];
          messagesRef.current = next;
          return next;
        });
        const initial = { step: 1, answers: {}, treePath };
        setOnboardingWizard(initial);
        setActiveTask("onboarding_wizard");
        setActiveOptionButtons([]);
        saveOnboardingWizardToStorage(initial);
        appendMessage({
          role: "assistant",
          kind: "consultation",
          content:
            "Opening the guided onboarding wizard. Complete each step below — idwiz AI runs only after you submit the full plan.",
        });
        return;
      }

      if (opt.id === "intent-browse-app-catalog") {
        setMessages((prev) => {
          const next = [...prev, userMsg];
          messagesRef.current = next;
          return next;
        });
        setSelectedTreePath(APPS_FIRST_DEFAULT_PATH);
        setCommandIntentLabel(APPS_FIRST_INTENT_HEADLINE);
        setActiveTask("OIN_SEARCH");
        setActiveFormFields([
          {
            id: "oinQuery",
            label: "Search the OIN catalog (installed apps)",
            type: "text",
            role: "oin_search",
            placeholder: "Min. 3 characters — e.g. slack, salesforce",
            required: false,
            submitOnEnter: true,
          },
        ]);
        setTaskFormValues({ ...EMPTY_TASK_FORM, oinQuery: "" });
        setActiveOptionButtons([]);
        setOinCatalogUi({ ...INITIAL_OIN_CATALOG_UI });
        setOinConfigureUi({ ...INITIAL_OIN_CONFIGURE });
        appendMessage({
          role: "assistant",
          kind: "consultation",
          content:
            "Search installed applications in your org. Results load via Okta only (`/api/idp/okta/oin-search`) — not through idwiz AI chat.",
          nextSteps: [],
          explainWhy: "",
          securityRecommendation: false,
        });
        return;
      }

      if (opt.id === "c-proceed-implementation") {
        setMessages((prev) => {
          const next = [...prev, userMsg];
          messagesRef.current = next;
          return next;
        });
        setActiveOptionButtons([]);
        queueMicrotask(async () => {
          const exec = approveAndExecuteRef.current;
          if (approveExecuteReadyRef.current && exec) {
            await exec();
            return;
          }
          setActiveFormFields(null);
          setActiveTask("review_group_rules");
          setTaskFormValues({ ...EMPTY_TASK_FORM });
          setGroupRulesView({
            status: "loading",
            rules: [],
            error: null,
            fetchedAt: null,
            progressSteps: [
              { id: "fetch", label: "Fetching group rules via SSWS", state: "active" },
              { id: "render", label: "Rendering rules table", state: "pending" },
            ],
          });
          await loadGroupRulesFromOkta({ announceMode: "chip", runSummaryAfter: true });
        });
        return;
      }

      if (opt.id === "c-pe-2" || opt.id === "c-gr-1" || opt.id === "c-review-existing-rules") {
        setMessages((prev) => {
          const next = [...prev, userMsg];
          messagesRef.current = next;
          return next;
        });
        setActiveFormFields(null);
        setActiveTask("review_group_rules");
        setGroupRulesView({
          status: "loading",
          rules: [],
          error: null,
          fetchedAt: null,
          progressSteps: [
            { id: "fetch", label: "Fetching group rules via SSWS", state: "active" },
            { id: "render", label: "Rendering rules table", state: "pending" },
          ],
        });
        setTaskFormValues({ ...EMPTY_TASK_FORM });
        setActiveOptionButtons([]);
        queueMicrotask(() => loadGroupRulesFromOkta({ announceMode: "chip", runSummaryAfter: true }));
        return;
      }

      if (opt.id === "intent-add-servicenow-oin") {
        const hint = extractServiceNowHints(messagesRef.current, updatedMemory);
        setMessages((prev) => {
          const next = [...prev, userMsg];
          messagesRef.current = next;
          return next;
        });
        setConversationMemory((m) => ({ ...m, consultationMode: false }));
        setProposedConfig(null);
        setActiveFormFields([
          {
            id: "instanceName",
            label: "Instance display name",
            required: true,
            type: "text",
            placeholder: "e.g. Production",
          },
          {
            id: "subdomain",
            label: "Subdomain",
            required: true,
            type: "text",
            placeholder: "e.g. idaxia",
          },
          {
            id: "baseUrl",
            label: "Instance base URL",
            required: true,
            type: "text",
            placeholder: "https://idaxia.service-now.com",
          },
        ]);
        setTaskFormValues({
          ...EMPTY_TASK_FORM,
          instanceName: "",
          subdomain: hint.subdomain,
          baseUrl: hint.baseUrl || (hint.subdomain ? `https://${hint.subdomain}.service-now.com` : ""),
        });
        setActiveTask("oin_servicenow");
        setActiveOptionButtons([]);
        appendMessage({
          role: "assistant",
          kind: "consultation",
          content:
            "ServiceNow (OIN): enter your instance display name, subdomain, and base URL below. When ready, choose Create — idwiz will add the app in Okta (no raw JSON shown here).",
          nextSteps: [],
          explainWhy: "",
          securityRecommendation: false,
        });
        return;
      }

      if (opt.id === "csv-profile-map-yes" || opt.id === "csv-profile-map-no") {
        const leaf = pathToLeaf(selectedTreePathRef.current);
        if (opt.id === "csv-profile-map-no") {
          csvPendingRef.current = null;
          setShowCsvButton(false);
          setActiveWorkflow(null);
          setActiveTask(null);
          setMessages((prev) => {
            const next = [...prev, userMsg];
            messagesRef.current = next;
            return next;
          });
          appendMessage({
            role: "assistant",
            kind: "consultation",
            content:
              "Understood. CSV import cancelled. Choose **Proceed with CSV Import** or **Automate onboarding → CSV** when you want to try again.",
            nextSteps: [],
            explainWhy: "",
            securityRecommendation: false,
          });
          setActiveOptionButtons(getConciergeChips(leaf));
          return;
        }

        const pending = csvPendingRef.current;
        if (!pending?.headers?.length) {
          csvPendingRef.current = null;
          setShowCsvButton(false);
          setActiveWorkflow(null);
          setActiveTask(null);
          setMessages((prev) => {
            const next = [...prev, userMsg];
            messagesRef.current = next;
            return next;
          });
          appendMessage({
            role: "assistant",
            kind: "error",
            content: "No CSV data in memory. Drop a file again.",
            nextSteps: [],
            explainWhy: "",
            securityRecommendation: false,
          });
          setActiveOptionButtons(getConciergeChips(leaf));
          return;
        }

        const { users, columnMapping, warnings } = buildUsersFromCsv(pending.headers, pending.rows);
        csvPendingRef.current = null;
        setMessages((prev) => {
          const next = [...prev, userMsg];
          messagesRef.current = next;
          return next;
        });
        if (users.length === 0) {
          const warnText = warnings.length ? warnings.join(" ") : "No rows could be turned into users.";
          setShowCsvButton(false);
          setActiveWorkflow(null);
          setActiveTask(null);
          appendMessage({
            role: "assistant",
            kind: "error",
            content: warnText,
            nextSteps: [],
            explainWhy: "",
            securityRecommendation: false,
          });
          setActiveOptionButtons(getConciergeChips(leaf));
          return;
        }

        const bodyObj = {
          users,
          columnMapping,
          sourceFileName: pending.fileName || null,
        };
        const bodyStr = JSON.stringify(bodyObj, null, 2);
        setProposedConfig({ format: "json", body: bodyStr });
        setActiveTask("csv_user_import");
        setConversationMemory((m) => ({
          ...m,
          consultationMode: false,
          proposedConfigDraft: { format: "json", body: bodyStr },
        }));
        const warnSuffix = warnings.length ? `\n\nNote: ${warnings.join(" ")}` : "";
        setShowCsvButton(false);
        setActiveWorkflow(null);
        appendMessage({
          role: "assistant",
          kind: "consultation",
          content: `Prepared ${users.length} user(s) from CSV. Use Create when you’re ready to add them to Okta.${warnSuffix}`,
          nextSteps: [],
          explainWhy: "",
          securityRecommendation: false,
        });
        setActiveOptionButtons(getConciergeChips(leaf));
        pushHiddenSystemEvent({
          type: "csv_import_proposed",
          role: "system",
          hidden: true,
          instruction: `Proposed ${users.length} user(s) for Okta create.`,
        });
        return;
      }

      let agentText = `The user selected discovery option "${display}" (id: ${entry.optionId ?? "n/a"}). Analyze this choice, decide the next step, refresh optionButtons (3–4 items), and merge proposedConfig incrementally using discoveryAnswers and proposedConfigDraft.`;
      if (entry.optionId === "add-single-user-manual") {
        agentText += ` The user wants to add a single user manually. You MUST respond with JSON: activeTask "manual_user_create", formFields for ids email, firstName, lastName (all required), and proposedConfig.body as a stringified Okta Create User JSON { "profile": { "firstName", "lastName", "email", "login" } } with empty strings initially and login matching email.`;
      }
      if (entry.optionId === "integrate-with-ad") {
        agentText += ` The user chose Active Directory integration. You MUST respond with JSON only: activeTask "ad_integration", and architectureSteps with at least three items: (1) kind "requirement" — download/install Okta AD Agent; (2) kind "requirement" — service account with Domain Admin or least-privilege AD read permissions; (3) kind "recommendation" — Delegated Authentication for seamless sign-in. Each step needs title and detail. Put narrative in "message".`;
      }
      if (entry.optionId === "configure-sso") {
        agentText += ` The user chose **Configure SSO**. Respond with JSON only: concrete SAML/OIDC SSO guidance (metadata exchange, ACS/Recipient URLs, audience, signing certs, OIDC redirect URIs), 3–4 optionButtons for next steps, and optional proposedConfig if helpful.`;
      }
      if (entry.optionId === "intent-create-oidc-app") {
        agentText += ` The user chose **Create OIDC App**. Respond with JSON only: OIDC client registration steps (application type, redirect URIs, sign-in flows, scopes, assignments), 3–4 optionButtons, optional proposedConfig.`;
      }
      if (entry.optionId === "intent-configure-saml") {
        agentText += ` The user chose **Configure SAML** from the apps-first palette. Respond with JSON only: SAML SP setup, metadata, attribute statements, 3–4 optionButtons, optional proposedConfig.`;
      }
      if (entry.optionId === "intent-add-servicenow-oin") {
        agentText += ` The user chose **Add ServiceNow (OIN)**. Respond with JSON only: activeTask "oin_servicenow", formFields with ids exactly **instanceName**, **subdomain**, **baseUrl** (all required, type text). Leave proposedConfig null — do not include HCL or large JSON. Put a one-line instruction in message.`;
      }

      setMessages((prev) => {
        const next = [...prev, userMsg];
        messagesRef.current = next;
        queueMicrotask(() => runAgent(agentText, updatedMemory, next));
        return next;
      });
    },
    [
      appendMessage,
      architectSendMessage,
      loadGroupRulesFromOkta,
      oktaConnected,
      onOktaExecutionResult,
      pushHiddenSystemEvent,
      releaseRateLimitGate,
      runAgent,
      setMessages,
      submitUserMessage,
      thinking,
    ],
  );

  const currentTaskTitle = useMemo(() => {
    if (activeTask === "manual_user_create") return "Create user in Okta";
    if (activeTask === "csv_user_import") return "Bulk import users from CSV";
    if (activeTask === "onboarding_wizard") return "Automate onboarding";
    if (activeTask === "ad_integration") return "Integrate with Active Directory";
    if (activeTask === "review_group_rules") return "Reviewing Group Rules";
    if (activeTask === "oin_servicenow") return "Add ServiceNow (OIN)";
    if (activeTask === "OIN_SEARCH") return "OIN catalog search";
    if (activeTask === "CUSTOM_SAML") return "Custom SAML application";
    if (formActive) return "Current task";
    return null;
  }, [activeTask, formActive]);

  const approveDisabledHint = useMemo(() => {
    if (approveExecuteReady) return null;
    if (readOnlyTerraformMode) return "Read-only Terraform mode — execution is disabled.";
    if (!sswsValidated) {
      return "Validate SSWS in the Legacy SSWS panel — Execute enables once the token is verified.";
    }
    if (!canTriggerExecute) {
      return "No executable proposal or automation task is active.";
    }
    if (activeTask === "manual_user_create") {
      return "Enter email, first name, and last name in the current task form.";
    }
    if (activeTask === "csv_user_import") {
      return "CSV proposal must include at least one user with email or login.";
    }
    if (activeTask === "oin_servicenow") {
      return "Fill instance name, subdomain, and a valid https base URL — Application Administrator (or equivalent) required unless using the SSWS execution bridge.";
    }
    if (hasProposedBody) {
      return "Proposal ready — Execute applies supported group and sign-on policy fragments via Okta APIs.";
    }
    return null;
  }, [
    approveExecuteReady,
    readOnlyTerraformMode,
    sswsValidated,
    canTriggerExecute,
    activeTask,
    hasProposedBody,
  ]);

  const lastAssistantBrief = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.hidden === true) continue;
      const c0 = typeof m.content === "string" ? m.content : "";
      if (
        c0.startsWith("[SYSTEM:") ||
        c0.startsWith("[SYSTEM_ERROR:") ||
        c0.startsWith("[PIVOT:") ||
        c0.startsWith("[FORM_SUBMITTED:")
      ) {
        continue;
      }
      if (m.role === "assistant" && m.kind !== "error" && m.kind !== "success_card") {
        const c = typeof m.content === "string" ? m.content : "";
        return c.length > 420 ? `${c.slice(0, 420)}…` : c;
      }
    }
    return "";
  }, [messages]);

  const approveAndExecute = useCallback(async (formDataOverride) => {
    const vals =
      formDataOverride && typeof formDataOverride === "object"
        ? { ...EMPTY_TASK_FORM, ...formDataOverride }
        : taskFormValues;
    const fromFormSubmit = Boolean(formDataOverride);

    if (fromFormSubmit) {
      setConversationMemory((m) => ({
        ...m,
        agentMode: "EXECUTION",
        consultationMode: false,
      }));
    }

    if (!fromFormSubmit && !approveExecuteReady) return;

    if (fromFormSubmit && activeTask === "oin_servicenow") {
      const i = (vals.instanceName || "").trim();
      const s = (vals.subdomain || "").trim();
      const b = (vals.baseUrl || "").trim();
      if (!i || !s || !b) return;
      try {
        const u = new URL(b);
        if (u.protocol !== "https:" && u.protocol !== "http:") return;
      } catch {
        return;
      }
    }
    if (fromFormSubmit && activeTask === "manual_user_create") {
      if (!(vals.email || "").trim() || !(vals.firstName || "").trim() || !(vals.lastName || "").trim()) return;
    }

    if (fromFormSubmit) {
      setTaskFormValues((prev) => ({ ...prev, ...formDataOverride }));
    }

    if (activeTask === "oin_servicenow") {
      setServicenowAppSubmitting(true);
      setPhase("executing");
      phaseRef.current = "executing";
      try {
        const result = await executeOktaAction(vals, { activeTask: "oin_servicenow" });
        const app = result?.app;
        const adminUrl = app?.adminConsoleUrl || "";
        const lbl = app?.label || vals.instanceName || "ServiceNow";
        appendMessage({
          role: "assistant",
          kind: "success_card",
          content: `${lbl} is now in your Okta org. Use the link below to open the integration in Okta Admin — you can finish SAML details and assignments there.`,
          adminConsoleUrl: adminUrl,
          appLabel: lbl,
          nextSteps: [],
          explainWhy: "",
          securityRecommendation: false,
        });
        setActiveFormFields(null);
        setActiveTask(null);
        setTaskFormValues({ ...EMPTY_TASK_FORM });
        setConversationMemory((prev) => ({
          ...prev,
          consultationMode: true,
        }));
        setActiveOptionButtons(getConciergeChips(pathToLeaf(selectedTreePathRef.current)));
        setPhase("gathering");
        phaseRef.current = "gathering";
      } catch (e) {
        const code = e?.errorCode != null ? String(e.errorCode) : "UNKNOWN";
        const summary = e?.message || "App creation failed";
        appendMessage({
          role: "assistant",
          kind: "error",
          content: `Okta API error ${code}: ${summary}`,
          nextSteps: [],
          explainWhy: "",
          securityRecommendation: false,
        });
        setPhase("gathering");
        phaseRef.current = "gathering";
      } finally {
        setServicenowAppSubmitting(false);
      }
      return;
    }

    if (activeTask === "manual_user_create") {
      const email = (vals.email || "").trim();
      setManualUserSubmitting(true);
      setPhase("executing");
      phaseRef.current = "executing";
      try {
        const result = await executeOktaAction(vals, { activeTask: "manual_user_create" });
        const link = result?.user?.profileUrl || "";
        const linkLine = link ? ` Open profile: ${link}` : "";
        appendMessage({
          role: "assistant",
          kind: "consultation",
          content: `User ${email} successfully created in Okta.${linkLine}`,
          nextSteps: [],
          explainWhy: "",
          securityRecommendation: false,
        });
        setActiveFormFields(null);
        setActiveTask(null);
        setTaskFormValues({ ...EMPTY_TASK_FORM });
        setProposedConfig(null);
        setConversationMemory((prev) => ({
          ...prev,
          proposedConfigDraft: null,
          consultationMode: true,
        }));
        setActiveOptionButtons(getConciergeChips(pathToLeaf(selectedTreePathRef.current)));
        setPhase("gathering");
        phaseRef.current = "gathering";
      } catch (e) {
        const code = e?.errorCode != null ? String(e.errorCode) : "UNKNOWN";
        const summary = e?.message || "User creation failed";
        appendMessage({
          role: "assistant",
          kind: "error",
          content: `Okta API error ${code}: ${summary}`,
          nextSteps: [],
          explainWhy: "",
          securityRecommendation: false,
        });
        setPhase("gathering");
        phaseRef.current = "gathering";
      } finally {
        setManualUserSubmitting(false);
      }
      return;
    }

    if (activeTask === "CUSTOM_SAML") {
      if (!fromFormSubmit && !approveExecuteReady) return;
      const rawLabel =
        [vals.appName, vals.applicationName, vals.label, vals.name].find((x) => String(x || "").trim()) || "";
      if (fromFormSubmit && !String(rawLabel).trim()) {
        appendMessage({
          role: "assistant",
          kind: "error",
          content: "Enter an application name (or label) so idwiz can create the SAML app in Okta.",
          nextSteps: [],
          explainWhy: "",
          securityRecommendation: false,
        });
        return;
      }
      if (fromFormSubmit) {
        setTaskFormValues((prev) => ({ ...prev, ...formDataOverride }));
      }
      setCustomSamlSubmitting(true);
      setPhase("executing");
      phaseRef.current = "executing";
      try {
        const orgFromProject = project?.org?.url ? String(project.org.url).trim() : "";
        const sessionOrg = oktaOrgUrl ? String(oktaOrgUrl).trim() : "";
        const resolvedOrgUrl = orgFromProject || sessionOrg;
        const fromVault = resolvedOrgUrl ? retrieveToken(resolvedOrgUrl) : null;
        const tokenFromProject = projectApiToken ? String(projectApiToken).trim() : "";
        const tokenFromSession = oktaApiToken ? String(oktaApiToken).trim() : "";
        const resolvedApiToken = tokenFromProject || fromVault || tokenFromSession;
        if (!resolvedOrgUrl || !resolvedApiToken) {
          throw new Error(
            "Okta connection required — reconnect your org with a valid API token to create applications.",
          );
        }
        if (!tokenFromSession && resolvedApiToken && resolvedOrgUrl) {
          try {
            setOktaSession({ orgUrl: resolvedOrgUrl, apiToken: resolvedApiToken });
          } catch {
            /* ignore */
          }
        }
        const result = await createCustomSamlApp(
          {
            appName: String(rawLabel).trim(),
            ssoUrl: vals.ssoUrl || vals.ssoAcsUrl || vals.acsUrl,
            audience: vals.audience,
            attributes: Array.isArray(vals.attributes) ? vals.attributes : undefined,
            projectId: project?.projectId || undefined,
            nameIdFormat: vals.nameIdFormat ?? vals.nameIDFormat,
          },
          { oktaUrl: resolvedOrgUrl, apiToken: resolvedApiToken },
        );
        const app = result?.app;
        const adminUrl = app?.adminConsoleUrl || "";
        const outLabel = app?.label || String(rawLabel).trim();
        appendMessage({
          role: "assistant",
          kind: "success_card",
          content: `${outLabel} is now in your Okta org as a SAML 2.0 application. Open it in Admin to finish ACS URLs, attribute mapping, and assignments.`,
          adminConsoleUrl: adminUrl,
          appLabel: outLabel,
          nextSteps: [],
          explainWhy: "",
          securityRecommendation: false,
        });
        pushHiddenSystemEvent({
          type: "saml_app_created",
          role: "system",
          hidden: true,
          instruction: `SAML app created in Okta (facts for follow-up): ${JSON.stringify(app)}`,
        });
        setActiveFormFields(null);
        setActiveTask(null);
        setTaskFormValues({ ...EMPTY_TASK_FORM });
        setProposedConfig(null);
        setConversationMemory((prev) => ({
          ...prev,
          proposedConfigDraft: null,
          consultationMode: true,
        }));
        setActiveOptionButtons(getConciergeChips(pathToLeaf(selectedTreePathRef.current)));
        setPhase("gathering");
        phaseRef.current = "gathering";
      } catch (e) {
        const code = e?.errorCode != null ? String(e.errorCode) : "UNKNOWN";
        const summary = e?.message || "SAML app creation failed";
        appendMessage({
          role: "assistant",
          kind: "error",
          content: `Okta API error ${code}: ${summary}`,
          nextSteps: [],
          explainWhy: "",
          securityRecommendation: false,
        });
        pushHiddenSystemEvent({
          type: "system_error",
          role: "system",
          hidden: true,
          instruction: `[SYSTEM_ERROR: Action failed — POST /api/idp/okta/apps/saml returned: ${summary}]`,
        });
        setPhase("gathering");
        phaseRef.current = "gathering";
      } finally {
        setCustomSamlSubmitting(false);
      }
      return;
    }

    if (activeTask === "csv_user_import") {
      let parsed;
      try {
        parsed = JSON.parse(proposedConfig?.body || "{}");
      } catch {
        appendMessage({
          role: "assistant",
          kind: "error",
          content: "Could not parse proposed JSON.",
          nextSteps: [],
          explainWhy: "",
          securityRecommendation: false,
        });
        return;
      }
      const users = parsed?.users;
      if (!Array.isArray(users) || users.length === 0) return;
      setCsvBulkSubmitting(true);
      setPhase("executing");
      phaseRef.current = "executing";
      try {
        let ok = 0;
        const failures = [];
        const staggerMs =
          Date.now() < tpmThrottleUntilRef.current ? 1200 : users.length > 1 ? 450 : 0;
        for (let i = 0; i < users.length; i++) {
          if (i > 0 && staggerMs > 0) await sleep(staggerMs);
          const u = users[i];
          const profile = u?.profile && typeof u.profile === "object" ? u.profile : u;
          if (!profile || typeof profile !== "object") {
            failures.push({ index: i + 1, detail: "Invalid profile" });
            continue;
          }
          try {
            await createOktaUser({ profile });
            ok++;
          } catch (e) {
            const em = profile.email || profile.login || "";
            failures.push({
              index: i + 1,
              email: em,
              message: e?.message || "Failed",
            });
          }
        }
        const failLines = failures.slice(0, 8).map((f) =>
          `  Row ${f.index}${f.email ? ` (${f.email})` : ""}: ${f.message || f.detail}`,
        );
        const more = failures.length > 8 ? `\n… and ${failures.length - 8} more` : "";
        appendMessage({
          role: "assistant",
          kind: "consultation",
          content: `Bulk import finished: ${ok} created, ${failures.length} failed.${failures.length ? `\n${failLines.join("\n")}${more}` : ""}`,
          nextSteps: [],
          explainWhy: "",
          securityRecommendation: false,
        });
        setProposedConfig(null);
        setActiveTask(null);
        setConversationMemory((prev) => ({
          ...prev,
          proposedConfigDraft: null,
          consultationMode: true,
        }));
        setActiveOptionButtons(getConciergeChips(pathToLeaf(selectedTreePathRef.current)));
        setPhase("gathering");
        phaseRef.current = "gathering";
      } finally {
        setCsvBulkSubmitting(false);
      }
      return;
    }

    const bodyStr = proposedConfig?.body?.trim();
    if (!bodyStr) {
      appendMessage({
        role: "assistant",
        kind: "error",
        content: "No proposed configuration to execute.",
        nextSteps: [],
        explainWhy: "",
        securityRecommendation: false,
      });
      return;
    }

    const obj = parseProposalObject(bodyStr);
    const hasG = obj ? proposalHasGroupSlice(obj) : false;
    const hasP = obj ? proposalHasPolicySlice(obj) : false;

    setPhase("executing");
    phaseRef.current = "executing";
    const lines = [];
    try {
      if (hasG || hasP) {
        const initialSteps = [];
        if (hasG) initialSteps.push({ id: "g", label: "Creating dynamic group", state: "active" });
        if (hasP) {
          initialSteps.push({
            id: "p",
            label: "Applying sign-on policy",
            state: hasG ? "pending" : "active",
          });
        }
        setExecutionProgress({ steps: initialSteps });
      }
      if (hasG) {
        const gRes = await applyProposalGroup(bodyStr);
        lines.push(`Group created: ${gRes?.group?.name || "ok"} (${gRes?.group?.id || ""}).`);
        if (hasP) {
          setExecutionProgress({
            steps: [
              { id: "g", label: "Creating dynamic group", state: "done" },
              { id: "p", label: "Applying sign-on policy", state: "active" },
            ],
          });
        } else {
          setExecutionProgress({ steps: [{ id: "g", label: "Creating dynamic group", state: "done" }] });
        }
      }
      if (hasP) {
        if (hasG) {
          await sleep(Date.now() < tpmThrottleUntilRef.current ? 1200 : 700);
        }
        try {
          const pRes = await applyProposalPolicy(bodyStr);
          lines.push(`Sign-on policy created: ${pRes?.policy?.name || "ok"} (${pRes?.policy?.id || ""}).`);
        } catch (pe) {
          const pm = pe?.message || "Policy step failed";
          lines.push(`Policy step: ${pm} (finish policy details in Okta Admin if needed).`);
        }
        setExecutionProgress({
          steps: [
            ...(hasG ? [{ id: "g", label: "Creating dynamic group", state: "done" }] : []),
            { id: "p", label: "Applying sign-on policy", state: "done" },
          ],
        });
      }
      if (!hasG && !hasP) {
        setExecutionProgress(null);
        appendMessage({
          role: "assistant",
          kind: "consultation",
          content:
            "This proposal does not match a supported Okta group or sign-on policy payload. Refine the JSON (group profile.name without user email/login, or Policy with name, conditions, and actions) or apply changes manually in Okta.",
          nextSteps: [],
          explainWhy: "",
          securityRecommendation: false,
        });
        return;
      }
      appendMessage({
        role: "assistant",
        kind: "success_card",
        content: `${lines.join(" ")}\n\nSuccess — lifecycle automation finished for the supported steps.`,
        nextSteps: [],
        explainWhy: "",
        securityRecommendation: false,
      });
      setProposedConfig(null);
      setConversationMemory((prev) => ({
        ...prev,
        proposedConfigDraft: null,
        consultationMode: true,
      }));
      setActiveOptionButtons(getConciergeChips(pathToLeaf(selectedTreePathRef.current)));
      window.setTimeout(() => setExecutionProgress(null), 2200);
    } catch (e) {
      const code = e?.errorCode != null ? String(e.errorCode) : "UNKNOWN";
      const summary = e?.message || "Automation failed";
      appendMessage({
        role: "assistant",
        kind: "error",
        content: `Okta API error ${code}: ${summary}`,
        nextSteps: [],
        explainWhy: "",
        securityRecommendation: false,
      });
      pushHiddenSystemEvent({
        type: "system_error",
        role: "system",
        hidden: true,
        instruction: `[SYSTEM_ERROR: Action failed — proposal automation (group/policy) returned: ${summary}]`,
      });
      setExecutionProgress(null);
    } finally {
      setPhase("gathering");
      phaseRef.current = "gathering";
    }
  }, [
    appendMessage,
    activeTask,
    approveExecuteReady,
    proposedConfig?.body,
    taskFormValues,
    project,
    projectApiToken,
    oktaOrgUrl,
    oktaApiToken,
    setOktaSession,
    pushHiddenSystemEvent,
    createCustomSamlApp,
  ]);

  const handleAppCreateSubmit = useCallback(
    async (values) => {
      const orgFromProject = project?.org?.url ? String(project.org.url).trim() : "";
      const sessionOrg = oktaOrgUrl ? String(oktaOrgUrl).trim() : "";
      const orgUrl = orgFromProject || sessionOrg;
      const fromVault = orgUrl ? retrieveToken(orgUrl) : null;
      const tokenFromProject = projectApiToken ? String(projectApiToken).trim() : "";
      const tokenFromSession = oktaApiToken ? String(oktaApiToken).trim() : "";
      const apiToken = tokenFromProject || fromVault || tokenFromSession;

      if (!apiToken) {
        await sendArchitectMessage(
          "[SYSTEM_ERROR: No API token available. Cannot create application. Ask the user to reconnect Okta with a valid SSWS token.]",
        );
        return;
      }

      const appType = values.appType || values.applicationType || "";
      const appName =
        values.appName || values.applicationName || values.name || values.label || "";
      const appUrl = values.appUrl || values.applicationUrl || values.url || values.ssoUrl || "";

      const t = appType.toLowerCase();
      const isSaml = t.includes("saml");
      const isSpa = t.includes("spa") || t.includes("single page");
      const isNative = t.includes("native") || t.includes("mobile");
      const isSwa = t.includes("swa") && !t.includes("oidc");

      setAppIntegrationSubmitting(true);
      setThinking(true);
      try {
        let endpoint;
        /** @type {Record<string, unknown>} */
        let body;

        if (isSwa) {
          if (!String(appUrl || "").trim()) {
            setThinking(false);
            setAppIntegrationSubmitting(false);
            await sendArchitectMessage(
              "[SYSTEM_ERROR: SWA requires a target URL (app URL / login page). Ask the user for the application URL to automate, then retry.]",
            );
            return;
          }
          endpoint = "/api/idp/okta/apps/swa";
          body = {
            oktaUrl: orgUrl,
            orgUrl,
            apiToken,
            projectId: project?.projectId,
            appName,
            appUrl: String(appUrl).trim(),
            loginUrl: values.loginUrl != null ? String(values.loginUrl).trim() : String(appUrl).trim(),
          };
        } else if (isSaml) {
          endpoint = "/api/idp/okta/apps/saml";
          body = {
            oktaUrl: orgUrl,
            orgUrl,
            apiToken,
            projectId: project?.projectId,
            appName,
            ssoUrl: appUrl || "https://example.com/sso/saml",
            audience: appUrl || "https://example.com",
            nameIdFormat: "email",
          };
        } else {
          endpoint = "/api/idp/okta/apps/oidc";
          body = {
            oktaUrl: orgUrl,
            orgUrl,
            apiToken,
            projectId: project?.projectId,
            appName,
            appUrl,
            applicationType: isSpa ? "browser" : isNative ? "native" : "web",
            grantTypes: isSpa ? ["authorization_code"] : ["authorization_code", "refresh_token"],
            redirectUris: appUrl
              ? [`${String(appUrl).replace(/\/$/, "")}/callback`, `${String(appUrl).replace(/\/$/, "")}/auth/callback`]
              : ["https://example.com/callback"],
            postLogoutUris: appUrl
              ? [`${String(appUrl).replace(/\/$/, "")}/logout`]
              : ["https://example.com/logout"],
            pkce: isSpa || isNative,
          };
        }

        const res = await fetch(endpoint, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        const data = await res.json().catch(() => ({}));

        setThinking(false);

        if (data.ok) {
          const labelOut = data.app?.label || appName;
          const appId = data.app?.id;
          const status = data.app?.status;
          const typeLabel = isSaml ? "SAML 2.0" : isSwa ? "SWA (BROWSER_PLUGIN)" : "OIDC";
          await sendArchitectMessage(
            `[SYSTEM: Application created successfully in Okta.
App name: "${labelOut}"
App ID: ${appId}
Type: ${typeLabel}
Status: ${status ?? "—"}
${
  isSaml
    ? "Next steps: configure the SP side with the metadata, assign users or groups, test SSO."
    : isSwa
      ? "Next steps: confirm the target URL and login URL in the app settings, assign users or groups, install the Okta browser plugin where required, and test sign-in."
      : "Next steps: copy the Client ID to the application, assign users or groups, test login."
}
Confirm this to the user and show the key configuration values they need.]`,
          );
        } else {
          const errText = data.errorSummary || data.error || data.message || "Unknown error";
          await sendArchitectMessage(
            `[SYSTEM_ERROR: App creation failed.
Error: ${errText}
Explain what went wrong to the user. Common causes: invalid redirect URI format, app name already exists, insufficient token scope (needs okta.apps.manage).
Suggest how to fix it.]`,
          );
        }
      } catch (err) {
        setThinking(false);
        await sendArchitectMessage(
          `[SYSTEM_ERROR: Network error creating app: ${/** @type {Error} */ (err)?.message || err}]`,
        );
      } finally {
        setAppIntegrationSubmitting(false);
      }
    },
    [oktaApiToken, oktaOrgUrl, project, projectApiToken, sendArchitectMessage, setThinking],
  );

  const handleUserCreateSubmit = useCallback(
    async (values) => {
      const orgFromProject = project?.org?.url ? String(project.org.url).trim() : "";
      const sessionOrg = oktaOrgUrl ? String(oktaOrgUrl).trim() : "";
      const orgUrl = orgFromProject || sessionOrg;
      const fromVault = orgUrl ? retrieveToken(orgUrl) : null;
      const tokenFromProject = projectApiToken ? String(projectApiToken).trim() : "";
      const tokenFromSession = oktaApiToken ? String(oktaApiToken).trim() : "";
      const apiToken = tokenFromProject || fromVault || tokenFromSession;

      if (!orgUrl || !apiToken) {
        await sendArchitectMessage("[SYSTEM_ERROR: No token for user creation]");
        return;
      }

      setThinking(true);
      try {
        const res = await fetch("/api/idp/okta/users", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            oktaUrl: orgUrl,
            apiToken,
            profile: {
              firstName: values.firstName ?? values.first_name ?? "",
              lastName: values.lastName ?? values.last_name ?? "",
              email: values.email ?? values.workEmail ?? "",
              login: values.email ?? values.workEmail ?? "",
            },
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (data.ok && data.user?.id) {
          const uid = data.user.id;
          await sendArchitectMessage(
            `[SYSTEM: User created successfully in Okta. Name: ${values.firstName ?? values.first_name ?? ""} ${values.lastName ?? values.last_name ?? ""}, Email: ${values.email ?? values.workEmail ?? ""}, User ID: ${uid}. Confirm to the user and suggest next steps: assign to groups, trigger MFA enrollment, or create another user.]`,
          );
        } else {
          await sendArchitectMessage(
            `[SYSTEM_ERROR: User creation failed: ${data.errorSummary || data.error || "Unknown error"}. Common causes: email already exists, invalid email format, missing required profile attributes. Explain and suggest fix.]`,
          );
        }
      } catch (err) {
        await sendArchitectMessage(
          `[SYSTEM_ERROR: Network error creating user: ${/** @type {Error} */ (err)?.message || err}]`,
        );
      } finally {
        setThinking(false);
      }
    },
    [oktaApiToken, oktaOrgUrl, project, projectApiToken, sendArchitectMessage, setThinking],
  );

  /** Primary Action on AI forms — inline Okta tasks delegate to approveAndExecute; otherwise values go to the architect. */
  const handleFormPrimaryAction = useCallback(
    async (formValues) => {
      const vals = formValues && typeof formValues === "object" ? formValues : {};
      if (
        activeTask === "CUSTOM_SAML" ||
        activeTask === "oin_servicenow" ||
        activeTask === "manual_user_create"
      ) {
        await approveAndExecute(vals);
        return;
      }
      if (looksLikeAppIntegrationSubmit(vals, activeFormFields)) {
        await handleAppCreateSubmit(vals);
        return;
      }
      const fieldBlob = JSON.stringify(activeFormFields || []);
      const valBlob = JSON.stringify(vals);
      const m365Hint =
        looksLikeM365TenantSubmission(vals) ||
        /m365|microsoft|office\s*365|tenant\s*domain|365/i.test(fieldBlob + valBlob);

      if (formHasDomainSemantic(vals, activeFormFields) && m365Hint) {
        const domain = String(
          vals.tenantDomain ?? vals.domain ?? vals.tenant ?? vals.microsoft365Domain ?? vals.TenantDomain ?? "",
        ).trim();
        await submitUserMessage(
          `[FORM_SUBMITTED: Tenant domain configured as "${domain || "(not set)"}". Continue with the integration setup using this domain.]`,
          { interactionMode: "chat" },
        );
        return;
      }

      if (formHasDomainSemantic(vals, activeFormFields)) {
        const domain = pickDomainFormValue(vals, activeFormFields);
        await submitUserMessage(
          `[FORM_SUBMITTED: domain="${domain}"] Configure the integration using this domain. Proceed with the full configuration now.`,
          { interactionMode: "chat" },
        );
        return;
      }

      const isUserCreate =
        ("firstName" in vals || "first_name" in vals) && ("email" in vals || "workEmail" in vals);
      if (isUserCreate) {
        await handleUserCreateSubmit(vals);
        return;
      }

      const fallbackText = `I've filled in the form with: ${Object.entries(vals)
        .map(([k, v]) => `${k}: "${v}"`)
        .join(", ")}. Please proceed.`;
      await submitUserMessage(fallbackText, { interactionMode: "chat" });
    },
    [
      activeFormFields,
      activeTask,
      approveAndExecute,
      handleAppCreateSubmit,
      handleUserCreateSubmit,
      submitUserMessage,
    ],
  );

  const handleOinConfigureFieldChange = useCallback((id, val) => {
    setOinConfigureUi((prev) => {
      if (prev.status !== "ready") return prev;
      return { ...prev, values: { ...prev.values, [id]: val } };
    });
  }, []);

  const handleOinConfigureCancel = useCallback(() => {
    setOinConfigureUi({ ...INITIAL_OIN_CONFIGURE });
  }, []);

  const handleOinConfigureSubmit = useCallback(
    async (formValues) => {
      if (oinConfigureUi.status !== "ready") return;
      const templateName = oinConfigureUi.app?.name != null ? String(oinConfigureUi.app.name).trim() : "";
      if (!templateName) return;

      const orgFromProject = project?.org?.url ? String(project.org.url).trim() : "";
      const sessionOrg = oktaOrgUrl ? String(oktaOrgUrl).trim() : "";
      const resolvedOrgUrl = orgFromProject || sessionOrg;
      const fromVault = resolvedOrgUrl ? retrieveToken(resolvedOrgUrl) : null;
      const tokenFromProject = projectApiToken ? String(projectApiToken).trim() : "";
      const tokenFromSession = oktaApiToken ? String(oktaApiToken).trim() : "";
      const resolvedApiToken = tokenFromProject || fromVault || tokenFromSession;

      if (!resolvedOrgUrl || !resolvedApiToken) {
        appendMessage({
          role: "assistant",
          kind: "error",
          content: "Okta org URL and API token are required to create this app.",
          nextSteps: [],
          explainWhy: "",
          securityRecommendation: false,
        });
        return;
      }

      if (!tokenFromSession && resolvedApiToken && resolvedOrgUrl) {
        try {
          setOktaSession({ orgUrl: resolvedOrgUrl, apiToken: resolvedApiToken });
        } catch {
          /* ignore */
        }
      }

      const tenant = { oktaUrl: resolvedOrgUrl, apiToken: resolvedApiToken };
      setOinConfigureSubmitting(true);
      try {
        const body = buildOinInstallBodyFromCurated({
          fieldDefs: oinConfigureUi.fieldDefs,
          values: formValues,
          signOnMode: oinConfigureUi.signOnMode || "SAML_2_0",
        });
        const created = await installOinTemplateApp(
          {
            appName: templateName,
            label: body.label,
            signOnMode: body.signOnMode,
            settings: body.settings,
            projectId: project?.projectId,
          },
          tenant,
        );
        const url = created?.app?.adminConsoleUrl;
        appendMessage({
          role: "assistant",
          kind: "success_card",
          content: `Created OIN app "${body.label}" (${templateName}).${url ? ` Open in Okta: ${url}` : ""}`,
          nextSteps: [],
          explainWhy: "",
          securityRecommendation: false,
        });
        setOinConfigureUi({ ...INITIAL_OIN_CONFIGURE });
      } catch (e) {
        appendMessage({
          role: "assistant",
          kind: "error",
          content: e?.message || "Could not create the app in Okta.",
          nextSteps: [],
          explainWhy: "",
          securityRecommendation: false,
        });
      } finally {
        setOinConfigureSubmitting(false);
      }
    },
    [appendMessage, oinConfigureUi, oktaApiToken, oktaOrgUrl, project, projectApiToken, setOktaSession],
  );

  const handleOinAddToOkta = useCallback(
    async (app) => {
      const templateName = app?.name != null ? String(app.name).trim() : "";
      const displayName =
        (app?.displayName != null && String(app.displayName).trim()) ||
        (app?.label != null && String(app.label).trim()) ||
        templateName;
      const rowId = app?.id != null ? String(app.id) : templateName;
      if (!templateName) {
        appendMessage({
          role: "assistant",
          kind: "error",
          content: "Cannot add this app — missing catalog template name from search results.",
          nextSteps: [],
          explainWhy: "",
          securityRecommendation: false,
        });
        return;
      }
      let pivotedToAgent = false;
      setOinAddingAppId(rowId);
      try {
        const orgFromProject = project?.org?.url ? String(project.org.url).trim() : "";
        const sessionOrg = oktaOrgUrl ? String(oktaOrgUrl).trim() : "";
        const resolvedOrgUrl = orgFromProject || sessionOrg;
        const fromVault = resolvedOrgUrl ? retrieveToken(resolvedOrgUrl) : null;
        const tokenFromProject = projectApiToken ? String(projectApiToken).trim() : "";
        const tokenFromSession = oktaApiToken ? String(oktaApiToken).trim() : "";
        const resolvedApiToken = tokenFromProject || fromVault || tokenFromSession;

        if (!resolvedOrgUrl || !resolvedApiToken) {
          appendMessage({
            role: "assistant",
            kind: "error",
            content:
              "Okta org URL and API token are required — connect your tenant to add an app from the catalog.",
            nextSteps: [],
            explainWhy: "",
            securityRecommendation: false,
          });
          return;
        }

        if (!tokenFromSession && resolvedApiToken && resolvedOrgUrl) {
          try {
            setOktaSession({ orgUrl: resolvedOrgUrl, apiToken: resolvedApiToken });
          } catch {
            /* ignore */
          }
        }

        const tenant = { oktaUrl: resolvedOrgUrl, apiToken: resolvedApiToken };
        const schemaPayload = await fetchOinAppSchema(templateName, tenant);
        const cu = schemaPayload?.curatedUiSchema;
        const curatedFields = Array.isArray(cu?.fields) ? cu.fields : [];

        if (curatedFields.length > 0) {
          const signOnMode =
            (cu?.signOnMode && String(cu.signOnMode).trim()) ||
            (Array.isArray(schemaPayload?.signOnModes) && schemaPayload.signOnModes.length
              ? String(schemaPayload.signOnModes[0])
              : Array.isArray(app?.signOnModes) && app.signOnModes.length
                ? String(app.signOnModes[0])
                : "SAML_2_0");

          setOinConfigureUi({
            status: "ready",
            app: {
              name: templateName,
              displayName,
              signOnModes: Array.isArray(app?.signOnModes) ? app.signOnModes : schemaPayload?.signOnModes || [],
            },
            schemaPayload,
            formFields: curatedFieldsToFormFields(curatedFields),
            fieldDefs: curatedFields,
            values: initialValuesFromCuratedFields(curatedFields),
            signOnMode,
            errorMessage: null,
            note: schemaPayload?.note != null ? String(schemaPayload.note) : null,
          });
        } else {
          const signOnModesStr = Array.isArray(app?.signOnModes) ? app.signOnModes.join(", ") : "";
          await submitUserMessage(
            `[PIVOT: User selected "${displayName}" from OIN catalog. ` +
              `App template name (OIN key): "${templateName}". ` +
              (signOnModesStr ? `Catalog sign-on modes: ${signOnModesStr}. ` : "") +
              `No idwiz curated UI for this template — fetch schema via idwiz POST /api/idp/okta/oin-schema for this appName, ask only for required fields, ` +
              `then execute add_oin_app with generalSettings/ssoSettings from the schema.]`,
            { interactionMode: "chat" },
          );
          pivotedToAgent = true;
        }
      } catch (e) {
        appendMessage({
          role: "assistant",
          kind: "error",
          content: `Could not load OIN schema for "${displayName}": ${e?.message || "Unknown error"}`,
          nextSteps: [],
          explainWhy: "",
          securityRecommendation: false,
        });
      } finally {
        setOinAddingAppId(null);
        if (pivotedToAgent && typeof dismissSuggestedActionSurface === "function") {
          dismissSuggestedActionSurface();
        }
      }
    },
    [
      appendMessage,
      dismissSuggestedActionSurface,
      oktaApiToken,
      oktaOrgUrl,
      project,
      projectApiToken,
      setOktaSession,
      submitUserMessage,
    ],
  );

  /** OIN catalog search — tenant GET /api/v1/catalog/apps (SSWS required). */
  const executeOinSearch = useCallback(
    async (query) => {
      const q = String(query || "").trim();
      if (q.length < 3) return;
      if (oinSearchInFlightRef.current) return;
      oinSearchInFlightRef.current = true;

      const orgFromProject = project?.org?.url ? String(project.org.url).trim() : "";
      const sessionOrg = oktaOrgUrl ? String(oktaOrgUrl).trim() : "";
      const resolvedOrgUrl = orgFromProject || sessionOrg;
      const fromVault = resolvedOrgUrl ? retrieveToken(resolvedOrgUrl) : null;
      const tokenFromProject = projectApiToken ? String(projectApiToken).trim() : "";
      const tokenFromSession = oktaApiToken ? String(oktaApiToken).trim() : "";
      const resolvedApiToken = tokenFromProject || fromVault || tokenFromSession;

      if (!resolvedOrgUrl || !resolvedApiToken) {
        setOinCatalogUi({
          ...INITIAL_OIN_CATALOG_UI,
          status: "error",
          lastQuery: q,
          apps: [],
          searchPerformed: true,
          errorMessage:
            "Okta org URL and API token are required — connect your tenant to search the OIN catalog.",
          catalogSource: null,
          catalogNote: null,
        });
        oinSearchInFlightRef.current = false;
        return;
      }

      if (!tokenFromSession && resolvedApiToken && resolvedOrgUrl) {
        try {
          setOktaSession({ orgUrl: resolvedOrgUrl, apiToken: resolvedApiToken });
        } catch {
          /* ignore */
        }
      }

      setPhase("recommending");
      phaseRef.current = "recommending";
      setConversationMemory((m) => ({
        ...m,
        consultationMode: false,
        agentMode: "EXECUTION",
      }));
      setOinCatalogUi({
        ...INITIAL_OIN_CATALOG_UI,
        status: "loading",
        lastQuery: q,
        searchPerformed: false,
        catalogSource: null,
        catalogNote: null,
      });
      setOinCatalogSearchBusy(true);
      try {
        const data = await searchCatalogApps(q, {
          oktaUrl: resolvedOrgUrl || undefined,
          apiToken: resolvedApiToken || undefined,
        });
        const apps = Array.isArray(data?.apps) ? data.apps : [];
        setOinCatalogUi({
          status: "done",
          lastQuery: q,
          apps,
          searchPerformed: true,
          errorMessage: null,
          catalogSource: data?.source ?? null,
          catalogNote: data?.note ?? null,
        });
      } catch (e) {
        const is429 = e?.status === 429;
        const msg = e?.message || "Catalog search failed.";
        setOinCatalogUi({
          ...INITIAL_OIN_CATALOG_UI,
          status: is429 ? "rate_limited" : "error",
          lastQuery: q,
          apps: [],
          searchPerformed: true,
          errorMessage: is429 ? "Backend is cooling down. Try again in a few moments." : msg,
          catalogSource: null,
          catalogNote: null,
        });
      } finally {
        setOinCatalogSearchBusy(false);
        oinSearchInFlightRef.current = false;
        setPhase("recommending");
        phaseRef.current = "recommending";
      }
    },
    [
      setConversationMemory,
      oktaOrgUrl,
      oktaApiToken,
      project,
      projectApiToken,
      setOktaSession,
    ],
  );

  const fetchOinResults = executeOinSearch;
  const handleOinCatalogSearchEnter = executeOinSearch;

  useEffect(() => {
    const appsContextWorkflow =
      activeTask === "OIN_SEARCH" ||
      activeTask === "CUSTOM_SAML" ||
      activeTask === "oin_servicenow" ||
      (formActive && Array.isArray(activeFormFields) && activeFormFields.some(isOinSearchFormField));
    if (appsContextWorkflow && !oinWorkflowActiveRef.current) {
      oinWorkflowActiveRef.current = true;
      const treeForApps =
        activeTask === "CUSTOM_SAML" ? "Applications > SAML" : APPS_FIRST_DEFAULT_PATH;
      setSelectedTreePath(treeForApps);
      setCommandIntentLabel(APPS_FIRST_INTENT_HEADLINE);
    } else if (!appsContextWorkflow) {
      oinWorkflowActiveRef.current = false;
    }
  }, [activeTask, formActive, activeFormFields]);

  useEffect(() => {
    approveExecuteReadyRef.current = approveExecuteReady;
  }, [approveExecuteReady]);

  useEffect(() => {
    approveAndExecuteRef.current = approveAndExecute;
  }, [approveAndExecute]);

  const getIdwizPayload = useCallback(() => {
    return {
      currentContext,
      selectedTreePath,
      conversationMemory,
      hiddenSystemEvents,
      visibleMessages: messages.filter((m) => !m.hidden),
      okta: oktaConnected
        ? {
            orgUrl: oktaOrgUrl,
            apiToken: oktaApiToken,
          }
        : null,
    };
  }, [
    conversationMemory,
    currentContext,
    hiddenSystemEvents,
    messages,
    selectedTreePath,
    oktaConnected,
    oktaOrgUrl,
    oktaApiToken,
  ]);

  onboardingWizardRef.current = onboardingWizard;

  const executionSubmitting =
    manualUserSubmitting ||
    csvBulkSubmitting ||
    servicenowAppSubmitting ||
    customSamlSubmitting ||
    appIntegrationSubmitting ||
    oinConfigureSubmitting;

  return {
    phase,
    setPhase,
    thinking,
    refiningAgentResponse,
    selectedTreePath,
    setSelectedTreePath,
    currentContext,
    conversationMemory,
    hiddenSystemEvents,
    getIdwizPayload,
    input,
    setInput,
    proposedConfig,
    setProposedConfig,
    messages,
    sendMessage,
    /** Send a user chat line and run the agent (same path as the custom input box). */
    submitUserMessage,
    forceQuotaRetryNow,
    applyNextStep,
    activeOptionButtons,
    approveAndExecute,
    canCreate,
    canApproveExecute,
    approveExecuteReady,
    manualUserSubmitting,
    executionSubmitting,
    handleCsvFile,
    canMutateTab,
    formActive,
    taskFormUsesInlinePrimary,
    activeFormFields,
    architectPrimaryActionLabel,
    activeTask,
    taskFormValues,
    setTaskFormValue,
    currentTaskTitle,
    lastAssistantBrief,
    approveDisabledHint,
    rateLimitError,
    clearDashboardTabErrorState,
    dailyLimitReached,
    dailyAiRequestCount,
    refreshDailyAiRequestCount,
    manualRetryPending,
    sendReadyPulse,
    inputWakeBounce,
    inputRef,
    handleInputChange,
    onboardingWizard,
    selectOnboardingSource,
    updateOnboardingGroupsText,
    continueOnboardingStep2,
    submitOnboardingWizardPlan,
    exitOnboardingWizard,
    commandIntentLabel,
    groupRulesView,
    executionProgress,
    handleFormPrimaryAction,
    handleOinAddToOkta,
    oinAddingAppId,
    handleOinCatalogSearchEnter,
    executeOinSearch,
    fetchOinResults,
    oinCatalogUi,
    oinCatalogSearchBusy,
    oinConfigureUi,
    oinConfigureSubmitting,
    handleOinConfigureFieldChange,
    handleOinConfigureSubmit,
    handleOinConfigureCancel,
    showCsvButton,
    activeWorkflow,
    chatHttp429Active,
    dismissSuggestedActionSurface,
    warning,
    bestPracticeViolation,
    dismissWarning,
    endSession,
    sendArchitectMessage,
    roadmapItems: project?.roadmap?.items ?? [],
    auditData,
    driftData,
    project,
    setAuditData: projectCtx?.setAuditData || null,
    setDriftData: projectCtx?.setDriftData || null,
    syncAfterAssessment,
    refreshProject: loadProjectFromCtx,
  };
}
